import { StorageKeys } from "../utils/storage/storage";
import * as logger from "../utils/logger";
import toastr from "../utils/toastr";
import { handleApi, sendToErrorTracker } from "../utils/error";
import Notifications, { UNCERTIFIED_HOS_NOTIFICATION_STRING, isNotificationDismissedRecently } from "../utils/notifications";
import { OpenCabWorkflowActions } from "../store/openCabWorkflow/actions";
import { makeTrailerCache, makeTrailerAttachmentCache, BaseCache } from "../utils/storage/cache";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { dateTimeComparator, getNextDayStart } from "../utils/date";
import { isPlaceholderCertified } from "../utils/placeholderVerification";
import DutyStatusLog, { IAnnotation, STANDARD_STATUSES, DISPLAY_STATUSES, MALFUNCTION_STATUSES, IDutyStatusLog,
    CYCLE_AND_OPERATING_ZONE_STATUSES, NOUSERID, ALL_HOS_STATUSES } from "../model/DutyStatusLog";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import * as common from "./common";
import { getCurrentVehicle, clearCurrentVehicle, getPreviousVehicle, setPreviousVehicle } from "../utils/device";
import idSerializer from "geotab-uuid";
import { getServerDate } from "../utils/storage/date";
import MalfunctionsHelper from "./malfunctions";
import { StatusDataCache } from "./hos.StatusDataCache";
import { IModifiedType } from "./sync.types";
import BaseSync, { type ISyncAddSet, type ISyncOptions, type TSyncSource } from "./base";
import type { IApiError, TMultiCallParam } from "../utils/api.interfaces";
import { extend, hashToArray, isEmpty } from "../utils/vanilla";
import constants from "../constants";
import { DataActions } from "../store/data/actions";
import { IoxActionTypes, IIoxActionDictionary } from "../store/data/types";
import {
    compareDatesWithSequenceIds, isSituationalDrivingClear, isSituationalDrivingStatus,
    SITUATIONAL_DRIVING_CLEAR, type THosMalfunction, type THosState, type THosStatus
} from "hos-tools";
import { DutyStatusLogService, IExemptionLogEditRequest } from "../model/DutyStatusLogs";
import {
    ITrailerAttachmentSearch, normalizeTrailerAttachmentResults, primeTrailerDeviceIdentity, toTrailerAttachmentGetCall
} from "./trailerAttachmentDeviceLink";
import type { ITrailerAttachment } from "../model/TrailerAttachment";
import { HosLogActions } from "../store/hosLogs/actions";
import { FeatureFlagName } from "../utils/featureFlagName";
import { HosAnnotationActions } from "../store/hosAnnotations/actions";
import { getClonedAnnotationsForUser } from "../utils/storeHelpers";
import { isArgumentException, isDuplicateException, isInvalidApiOperationException, isInvalidRequestException,
    isOverLimitException, isRelationViolatedException } from "../utils/errorHandler/error.types";
import * as errorMessages from "../utils/errorHandler/error.messages";
import { TrailerAttachment } from "../model/TrailerAttachment";
import type { ITrailer } from "../model/Trailer";
import { OdometerData } from "./hos.OdometerData";
import type { TVehicle } from "../store/vehicleHistory/types";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import { removeHiddenRequestedAdds } from "./hosSyncHelpers";

const NOOP = () => {};

const LOG_ID_LOOKUP_CHUNK_SIZE = 50;
interface ISlimDutyStatusLog {
    id: string;
    annotations?: IAnnotation[];
    verifyDateTime?: string;
    editDateTime?: string;
}

interface IReassignApiParams {
    logsToMove: ISlimDutyStatusLog[];
    isUnassigningLogs?: boolean;
}

const NO_DEVICE_ID = "NoDeviceId";
const NO_USER_ID = "NoUserId";

const UNIDENTIFIED_STATUSES: THosStatus[] = ["D", "ON", "INT_D", "EnginePowerup", "EngineShutdown"];
const ANNOTATED_STATES: THosState[] = ["Active", "Requested"];
const MALFUNCTION_TYPES: THosMalfunction[] = ["None", "Diagnostic", "Malfunction", "UserClear", "SystemClear",
                                            "SystemClearDriving", "ManualPosition", "SystemDiagnosticClear", "SystemDiagnosticClearDriving",
                                            "UserDiagnosticClear", "UserMalfunctionClear", "DiagnosticManualPosition", "MalfunctionManualPosition"];
const NEW_ON_PROMPT_STATUSES: THosStatus[] =  ["D", "ON", "MotionStopped", "OFF", "EnginePowerupPC"];

// MYG-123440: Temporarily set to undefined to remove the limit while a permanent fix is developed.
// Was: HOS_RESULTS_LIMIT = 2000, UNIDENTIFIED_RESULTS_LIMIT = 500
const HOS_RESULTS_LIMIT: number | undefined = undefined;
const UNIDENTIFIED_RESULTS_LIMIT: number | undefined = undefined;

interface IDutyStatusLogQueryParams {
    typeName: string;
    fromVersion?: string | null;
    search: {
        userSearch: { id: string } | null;
        deviceSearch: { id: string } | null;
        statuses?: string[] | null;
        fromDate?: Date | string | null;
        toDate?: Date | string | null;
        includeBoundaryLogs?: boolean | null;
        includeCompliance?: boolean;
        includeModifications: boolean;
        includeLocations: boolean;
    };
    resultsLimit?: number;
}

export default class HosSync extends BaseSync {
    readonly storageKey = StorageKeys.USER_LOGS_KEY;
    readonly storageSystemKey = StorageKeys.SYSTEM_LOGS_KEY;
    name = "Sync: HOS";
    private driverVersion: string;
    private automaticLog: DutyStatusLog | null = null;
    private readonly trailerCache: BaseCache<ITrailer>;
    private readonly trailerAttachmentCache: BaseCache<TrailerAttachment>;
    private readonly statusCache: StatusDataCache;
    private readonly odometerData: OdometerData;
    private firstCall = true;
    private readonly pendingAnnotationAdds = new Set<string>();

    private newLogs: DutyStatusLog[];

    constructor(
        options: ISyncOptions,
        private readonly dutyStatusLogService: DutyStatusLogService,
        private readonly malfunctionsHelper: MalfunctionsHelper
    ) {
        super(options);
        // MYG-148762: see the note in DvirSync's constructor.
        primeTrailerDeviceIdentity(this.store);
        this.automaticLog = null;
        this.trailerCache = makeTrailerCache(this.store);
        this.trailerAttachmentCache = makeTrailerAttachmentCache(this.store);
        this.statusCache = new StatusDataCache(this.api, this.store);
        this.odometerData = new OdometerData(this.api, this.store);
    }

    private get doubleRetentionStartDate () {
        return this.hosProcess.getRetentionStartDate(2);
    }

    getPcYmCalls(groupedPcYmEdits: IExemptionLogEditRequest[]) {
        const calls: [IModifiedType, ISyncAddSet<IDutyStatusLog>][] = [];
        groupedPcYmEdits.forEach(group => {
            const exemptionLog = group.editedExemptionLog;
            exemptionLog && calls.push(["Set", { typeName: "DutyStatusLog", entity: this.cleanLog(exemptionLog) }]);

            if (group.logsToEdit.length) {
                calls.push(...group.logsToEdit.map(logToEdit => {
                    return ["Set", {
                        typeName: "DutyStatusLog",
                        entity: this.cleanLog(logToEdit)
                    }] as [IModifiedType, ISyncAddSet<IDutyStatusLog>];
                }));
            }

            if (group.logsToRemove.length) {
                calls.push(...group.logsToRemove.map(logToRemove => {
                    return ["Remove", {
                        typeName: "DutyStatusLog",
                        entity: this.cleanLog(logToRemove)
                    }] as [IModifiedType, ISyncAddSet<IDutyStatusLog>];
                }));
            }
        });
        return calls;
    }

    pushResolvedPcYmEditsToServer(calls: [IModifiedType, ISyncAddSet<IDutyStatusLog>][], groupedPcYmEdits: IExemptionLogEditRequest[], localLogs: IIdDictionary<DutyStatusLog>) {
        // Edits are accepted in groups, similar to how we reassign requested duty status logs.
        // If the initial log is successfully accepted or rejected, we attempt to resolve the edits / remove the logs if needed the best we can.
        // If the initial log errors, we need to cancel ALL edits related to that log and make the user accept/reject it again.
        return new Promise((resolve, reject) => {
            const handleAlreadySyncedLogs = (failedLogIndex?: number) => {
                if (!groupedPcYmEdits.length) {
                    return;
                }

                const completedCalls = failedLogIndex !== undefined ? calls.slice(0, failedLogIndex) : calls;
                if (!completedCalls.length) {
                    return;
                }

                // Exemption logs are either edited or removed
                const exemptionLogEditsSynced = completedCalls
                    .map(call => this.isMainExemptionLogEdit(call) ? call[1].entity : null)
                    .filter(call => !!call);

                // If first exemption log is missing in calls, last stopping point was probably a failure for the inbetween logs. Add it back to the list to process
                if (!exemptionLogEditsSynced[0] || exemptionLogEditsSynced[0].id !== groupedPcYmEdits[0].editedExemptionLog?.id) {
                    exemptionLogEditsSynced.unshift(groupedPcYmEdits[0].editedExemptionLog as DutyStatusLog);
                }

                if (failedLogIndex !== undefined) {
                    const failedCall = calls[failedLogIndex];
                    // If the failed call is not an exemption log, remove the last exemption log as it is grouped with the failing call and cannot be processed yet
                    if (!exemptionLogEditsSynced.some(log => log?.id === failedCall[1].entity.id)) {
                        const failureGroupMainLog = exemptionLogEditsSynced.pop();

                        // Also find the failed call and remove it from its grouping
                        const failureGroup = groupedPcYmEdits.find(group => group.editedExemptionLog?.id === failureGroupMainLog?.id);
                        const failedCallId = failedCall[1].entity.id;
                        if (failureGroup) {
                            failureGroup.logsToEdit = failureGroup.logsToEdit.filter(log => log.id !== failedCallId);
                            failureGroup.logsToRemove = failureGroup.logsToRemove.filter(log => log.id !== failedCallId);
                        }
                    }
                }

                if (exemptionLogEditsSynced.length > 0) {
                    groupedPcYmEdits.forEach(groupedPcYmEdit => {
                        const editedExemptionLog = groupedPcYmEdit.editedExemptionLog;
                        const isAccepted = editedExemptionLog?.state === "Active";

                        if (editedExemptionLog && exemptionLogEditsSynced.find(log => log?.id === editedExemptionLog.id)) {
                            const logId = this.getLogId(editedExemptionLog);
                            if (editedExemptionLog.isRequestedRemoval && isAccepted) {
                                delete localLogs[logId];
                            } else {
                                this.cleanRequestedLog(localLogs[logId]);
                            }

                            if (groupedPcYmEdit.logsToEdit.length) {
                                if (isAccepted) {
                                    groupedPcYmEdit.logsToEdit.forEach(log => {
                                        this.cleanRequestedLog(localLogs[this.getLogId(log)]);
                                    });
                                } else {
                                    groupedPcYmEdit.logsToEdit.forEach(log => {
                                        delete localLogs[this.getLogId(log)];
                                    });
                                }
                            }

                            // Shouldn't have any logsToRemove if the exemption log edit was rejected, but just in case
                            if (groupedPcYmEdit.logsToRemove.length && isAccepted) {
                                groupedPcYmEdit.logsToRemove.forEach(log => {
                                    delete localLogs[this.getLogId(log)];
                                });
                            }
                        }
                    });

                    this.setLogs(localLogs);
                    groupedPcYmEdits.splice(0, exemptionLogEditsSynced.length);
                }
            };
            const handleSuccess = () => {
                handleAlreadySyncedLogs();
                resolve(localLogs);
            };

            if (calls.length === 0) {
                handleSuccess();
                return;
            }

            logger.log(logger.Source.SYNC, common.formatCallForLogger("DutyStatusLogPcYmRequest", calls), this.currentUser);
            this.api.multiCall(calls, () => handleSuccess(), errorObject => {
                if (isOverLimitException(errorObject)) {
                    reject(errorObject);
                    return;
                }

                const requestIndex = errorObject?.data?.requestIndex;
                const call = requestIndex !== undefined && calls[requestIndex];
                const failedLog = call && call[1].entity;
                const logId = failedLog && this.getLogId(failedLog);

                if (!failedLog || !logId || !localLogs.hasOwnProperty(logId)) {
                    reject(errorObject);
                    return;
                }

                handleAlreadySyncedLogs(requestIndex);
                if (this.isMainExemptionLogEdit(call)) {
                    // Failure resolving main exemption log edit - reset all other edits; driver will need to accept again (if logs still exist)
                    const groupedLogs = groupedPcYmEdits.find(group => group.editedExemptionLog?.id === failedLog.id);
                    if (groupedLogs) {
                        this.resetLogToCorrectState([groupedLogs.editedExemptionLog as IDutyStatusLog].concat(groupedLogs.logsToEdit), localLogs)
                            .then(() => {
                                if (groupedLogs.logsToRemove.length) {
                                    let wereLogsUpdated = false;
                                    groupedLogs.logsToRemove.forEach(log => {
                                        const removeLogId = this.getLogId(log);
                                        if (localLogs.hasOwnProperty(removeLogId)) {
                                            delete localLogs[removeLogId].modified;
                                            if (!wereLogsUpdated) {
                                                wereLogsUpdated = true;
                                            }
                                        }
                                    });

                                    if (wereLogsUpdated) {
                                        this.setLogs(localLogs);
                                    }
                                }

                                const groupedCallsWithoutFailure = groupedPcYmEdits.filter(group => group.editedExemptionLog?.id !== failedLog.id);
                                const newCalls = this.getPcYmCalls(groupedCallsWithoutFailure);
                                return this.pushResolvedPcYmEditsToServer(newCalls, groupedCallsWithoutFailure, localLogs).then(resolve, reject);
                            }, reject);
                    } else {
                        // Don't know what to do here
                        reject(errorObject);
                    }
                } else {
                    // failure during follow up logs - try to finish remaining
                    if (errorMessages.isNotInSystemException(errorObject) && isRelationViolatedException(errorObject)) {
                        delete localLogs[logId];
                        const newCalls = calls.slice(requestIndex + 1);
                        this.pushResolvedPcYmEditsToServer(newCalls, groupedPcYmEdits, localLogs).then(resolve, reject);
                    } else {
                        logger.log(logger.Source.SYNC, `Error: trying to edit log with a pending edit request, logId: ${ failedLog.id }`, this.currentUser);
                        this.resetInvalidEdit(localLogs, failedLog)
                            .then(() => {
                                const newCalls = calls.slice(requestIndex + 1);
                                return this.pushResolvedPcYmEditsToServer(newCalls, groupedPcYmEdits, localLogs);
                            })
                            .then(resolve, reject);
                    }
                }
            });
        });
    }

    resolvePcYmEdits(localLogs: IIdDictionary<DutyStatusLog>): Promise<IIdDictionary<DutyStatusLog>> {
        return new Promise<IIdDictionary<DutyStatusLog>>((resolve, reject) => {
            const groupedLogsToResolve: IExemptionLogEditRequest[] = [];
            const isEditableExemptionLog = (log: DutyStatusLog) => log.status === "PC" || log.status === "YM";
            const getRequestedEditForRemoval = (log?: DutyStatusLog) => log?.isRequestedRemoval
                ? log.requestedEdit
                : undefined;
            const getRequestedEditForSdc = (log?: DutyStatusLog) => log?.status === SITUATIONAL_DRIVING_CLEAR ? log.requestedEdit : undefined;

            let currentRequestGroup: IExemptionLogEditRequest = {
                editedExemptionLog: null,
                logsToEdit: [],
                logsToRemove: []
            };

            const sortedLogs = hashToArray(localLogs)
                .filter(log => log.driver.id === this.currentUser?.id)
                .map(log => {
                    // The SDC needs to have the original datetime for the next bit of logic to work correctly
                    if (log.status === SITUATIONAL_DRIVING_CLEAR && log._originalDateTimeBeforeEdit) {
                        return new DutyStatusLog({ ...log, dateTime: log._originalDateTimeBeforeEdit });
                    }
                    return log;
                })
                .sort(compareDatesWithSequenceIds);

            sortedLogs.forEach(log => {
                if (log.placeholder) {
                    return;
                }

                if (isEditableExemptionLog(log)) {
                    if (!currentRequestGroup.editedExemptionLog) {
                        currentRequestGroup.editedExemptionLog = log;
                        return;
                    }

                    // Support for multiple of the same exemption log in one range
                    if (log.status === currentRequestGroup.editedExemptionLog.status && log.device.id === currentRequestGroup.editedExemptionLog.device.id) {
                        if (log.modified === "Remove") {
                            currentRequestGroup.logsToRemove.push(log);
                        }
                        return;
                    }
                }

                if (currentRequestGroup.editedExemptionLog) {
                    if (log.requestedAdd || log.modified === "Add") {
                        if (this.dutyStatusLogService.isReviewed(log)) {
                            currentRequestGroup.logsToEdit.push(log);
                        }
                        return;
                    }

                    if (log.status === "INT_PC" && log.modified === "Remove") {
                        currentRequestGroup.logsToRemove.push(log);
                        return;
                    }

                    const isSdcLog = isSituationalDrivingClear(log.status);
                    if (isSdcLog || ALL_HOS_STATUSES.indexOf(log.status) > -1) {
                        const exemptionLogEditRequest = getRequestedEditForRemoval(currentRequestGroup.editedExemptionLog);
                        const sdcEditRequest = getRequestedEditForSdc(log);
                        if (log.modified === "Remove") {
                            currentRequestGroup.logsToRemove.push(log);
                        }

                        const editRequestToProcess = exemptionLogEditRequest || sdcEditRequest;
                        if (editRequestToProcess && this.dutyStatusLogService.isReviewed(editRequestToProcess)) {
                            currentRequestGroup.editedExemptionLog = editRequestToProcess;
                            groupedLogsToResolve.push(currentRequestGroup);
                        } else if (currentRequestGroup.editedExemptionLog.modified !== "Remove" && !(isSdcLog && log.modified === "Set")) {
                            // Not a valid edit and not the user trying to edit their own logs.
                            // Remove the modified tag from the removed logs so we do not remove any logs incorrectly.
                            if (currentRequestGroup.logsToRemove.length) {
                                currentRequestGroup.logsToRemove.forEach(logToRemove => {
                                    delete localLogs[this.getLogId(logToRemove)].modified;
                                });
                                this.setLogs(localLogs);
                            }
                        }

                        // Previous period complete, start a new exemption period
                        currentRequestGroup = {
                            editedExemptionLog: isEditableExemptionLog(log) ? log : null,
                            logsToEdit: [],
                            logsToRemove: []
                        };
                    }
                }
            });

            if (groupedLogsToResolve.length) {
                this.pushResolvedPcYmEditsToServer(this.getPcYmCalls(groupedLogsToResolve), groupedLogsToResolve, localLogs).then(resolve, reject);
                return;
            }
            resolve(localLogs);
        });
    }

    resolveRequestedEdits(localLogs: IIdDictionary<DutyStatusLog>): Promise<IIdDictionary<DutyStatusLog>> {
        return new Promise<IIdDictionary<DutyStatusLog>>((resolve, reject) => {
            let resolvedRequests: DutyStatusLog[] = [];
            let allResolvedRequests: DutyStatusLog[] = [];
            Object.keys(localLogs).forEach(logId => {
                let log = localLogs[logId];

                if (!log.id && !log.placeholder) {
                    throw new Error("Attempting to sync a record without an ID");
                }

                if (!log.placeholder) {
                    if (log.requestedEdit && this.dutyStatusLogService.isReviewed(log.requestedEdit)) {
                        let newRequestedEdit = new DutyStatusLog(log.requestedEdit);
                        let requestedEdit = newRequestedEdit.clone();
                        let newDriverId = log.requestedEdit.driver.id;
                        requestedEdit.driver = { "id": newDriverId };
                        if (requestedEdit.state !== "Active" || !log.originalDriverBeforeEdit) {
                            resolvedRequests.push(requestedEdit);
                            allResolvedRequests.push(requestedEdit);
                        }
                    } else if (log.requestedAdd && this.dutyStatusLogService.isReviewed(log)) {
                        resolvedRequests.push(log);
                        allResolvedRequests.push(log);
                    }
                }
            });
            if (!resolvedRequests.length) {
                resolve(localLogs);
                return;
            }

            let calls: [IModifiedType, ISyncAddSet<DutyStatusLog>][] = resolvedRequests.map(resolvedRequest => {
                return ["Set", {
                    typeName: "DutyStatusLog",
                    entity: resolvedRequest
                }];
            });

            if (calls.length > 0) {
                this.pushRequestedChangesToServer(calls, allResolvedRequests, localLogs).then(resolve, reject);
            } else {
                resolve(localLogs);
            }
        });
    }

    mergeDutyStatusLogs(serverLogs: DutyStatusLog[], localLogs: IIdDictionary<DutyStatusLog>, force?: boolean, isFromMalfunctionSync?: boolean): IIdDictionary<DutyStatusLog> {
        let apiUserId = this.currentUser ? this.currentUser.id : null;
        let shouldCopyMalfunctionProperty = (logFromLocal: DutyStatusLog, logFromServer: DutyStatusLog) => logFromLocal.malfunction
                                                                                                            && logFromLocal.malfunction !== "None"
                                                                                                            && logFromServer.malfunction === "None";
        const isInitialDownload = this.firstCall || force;
        const hasFeedVersion = !!common.getFeedVersion(this.api, this.store, common.FeedTypeKey.HOS);
        const shouldLogDelays = !isFromMalfunctionSync && serverLogs.length && !isInitialDownload && hasFeedVersion;
        const shouldLogDelayForLog = (log: DutyStatusLog) => shouldLogDelays && log.origin === "Automatic" && apiUserId && log.driver.id === apiUserId && !log.requestedEdit;
        const newLogCreationDates: string[] = [];

        // Merge in server logs with local logs
        // eslint-disable-next-line complexity
        serverLogs.forEach(logging => {
            let log = new DutyStatusLog(logging);

            this.syncLogger.fromServer({
                dateTime: log.dateTime,
                status: log.status,
                device: log.device.id,
                driver: log.driver.id
            });
            // Server log exists locally
            if (localLogs[logging.id]) {
                // Conflict resolution
                const local = new DutyStatusLog(localLogs[log.id]);
                const previousState = local.getPreviousState();
                const modifiedState = local.modified;
                const originalDriverBeforeEdit = local.originalDriverBeforeEdit;

                /*
                    Four scenarios:
                    local changed, server same => take local
                    local changed, server changed => conflict if local was "edit", take server if local was "add"
                    local same, server changed => take server
                    local same, server same => do nothing
                    */

                // Local changed
                if (modifiedState && previousState) {
                    // Server changed
                    if (previousState.version !== log.version || previousState.getRequestedVersion() !== log.getRequestedVersion()) {
                        switch (modifiedState) {
                            case "Add":
                                // Server has a different version, merge them
                                logger.log(logger.Source.SYNC, "Merged local HOS log with server", this.currentUser);
                                localLogs[log.id] = common.merge<DutyStatusLog>(localLogs[log.id], logging, {
                                    modified: "Set",
                                    fromServer: true
                                });

                                break;
                            case "Set": {
                                // Diagnostic fields must read `local`/`previousState`, not `localLogs[log.id]`,
                                // which the resolution logic below this switch is about to overwrite.
                                let conflictMessage = `SYNC: HOS conflict (both server and client changed). Operation: ${ modifiedState }, Local ID: ${ local.id }, Server ID: ${ log.id }`;

                                if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_HOS_SYNC_TRACING)) {
                                    const verifyDateTimeComparison = !local.verifyDateTime && !log.verifyDateTime
                                        ? "neither"
                                        : local.verifyDateTime === log.verifyDateTime
                                            ? "equal"
                                            : !local.verifyDateTime
                                                ? "server-only"
                                                : !log.verifyDateTime
                                                    ? "local-only"
                                                    : "differing";

                                    conflictMessage = `SYNC_HOS_CONFLICT op=${ modifiedState } localId=${ local.id } serverId=${ log.id } ` +
                                        `syncCounter=${ local._syncCounter } verifyDT=${ verifyDateTimeComparison } ` +
                                        `hadVerifyDateTimeBeforeConflict=${ !!previousState.verifyDateTime } ` +
                                        `version.local=${ local.version } version.previous=${ previousState.version } version.server=${ log.version } ` +
                                        `serverState=${ log.state }`;
                                }

                                logger.event(logger.Event.SYNCHOSCONFLICT, conflictMessage);
                                break;
                            }
                            case "Remove":
                                logger.event(
                                    logger.Event.SYNCHOSCONFLICT,
                                    `SYNC: HOS conflict (both server and client changed). Operation: ${ modifiedState }, Local ID: ${ local.id }, Server ID: ${ log.id }`
                                );
                                break;
                            default:
                                break;
                        }
                    }
                } else if (
                    previousState &&
                    log.requestedEdit &&
                    (originalDriverBeforeEdit ||
                        (previousState.version !== log.version && previousState.getRequestedVersion() === log.getRequestedVersion()))
                ) {
                    logger.event(logger.Event.SYNCHOSCONFLICT, `SYNC: HOS conflict (both server and client changed). Local ID: ${ local.id }, Server ID: ${ log.id }`);
                } else {
                    // Local not modified
                    if (local.version !== log.version || local.getRequestedVersion() !== log.getRequestedVersion()) {
                        logger.log(logger.Source.SYNC, "Updated local HOS log from server", this.currentUser);
                        logging.fromServer = true;
                        if (logging.state === "Inactive" || logging.state === "Rejected") {
                            delete localLogs[logging.id];
                        } else {
                            let localLog = localLogs[logging.id];
                            // If it's already marked as a malfunction/diagnostic previously, don't want to show the manual location input prompt again
                            if (shouldCopyMalfunctionProperty(localLog, logging)) {
                                logging.malfunction = localLog.malfunction;
                            }

                            localLogs[logging.id] = logging;
                        }
                    }
                }
            } else if (logging.state !== "Inactive" && logging.state !== "Rejected") {
                logging.fromServer = true;
                localLogs[logging.id] = logging;
                // Check for special condition logs- auto ON and auto OFF, as we may need to pop a prompt
                // Only for active logs that belong to the user
                // Only if the log is within 10 min of current time
                if (this.isAutomaticLog(logging, apiUserId)) {
                    this.automaticLog = logging;
                }

                if (shouldLogDelayForLog(log)) {
                    let logCreationDate = log._createdDate || log.editDateTime;
                    logCreationDate && newLogCreationDates.push(logCreationDate);
                }
            }
        });

        newLogCreationDates.length && this.logDownloadDelays(newLogCreationDates);
        return localLogs;
    }

    logDownloadDelays(newLogCreationDates: string[]) {
        const now = getServerDate().getTime();
        const getDelay = (logCreationDate: string) => {
            let logDelay = (now - new Date(logCreationDate || now).getTime()) / 1000;
            return logDelay > 0 ? logDelay : 0;
        };

        let logDelaySecondsArray = newLogCreationDates.map(log => getDelay(log)).sort((a, b) => a - b);
        let maxDelay = logDelaySecondsArray[logDelaySecondsArray.length - 1];
        let minDelay = logDelaySecondsArray[0];
        let medianDelay = logDelaySecondsArray[Math.floor(logDelaySecondsArray.length / 2)];
        let avgDelay = Math.floor((logDelaySecondsArray.reduce((totalDelay, currentDelay) => totalDelay + currentDelay, 0) / logDelaySecondsArray.length) * 1000) / 1000;
        this.syncLogger.setDelay({
            min: minDelay,
            max: maxDelay,
            median: medianDelay,
            avg: avgDelay,
            newLogCount: newLogCreationDates.length
        });
    }

    isAutomaticLog(log: DutyStatusLog, apiUserId: string | null) {
        const isActiveState = log.state === "Active";
        const hasValidDriverId = log.driver.id !== NO_USER_ID;
        const hasMatchingUserId = apiUserId && apiUserId === log.driver.id;
        const isAutomaticOrigin = log.origin === "Automatic";
        const isNewPromptStatus = NEW_ON_PROMPT_STATUSES.indexOf(log.status) > -1;
        const isRecentDateTime = getServerDate().getTime() - new Date(log.dateTime).getTime() <= 10 * 60 * 1000;

        return !!(isActiveState &&
            hasValidDriverId &&
            hasMatchingUserId &&
            isAutomaticOrigin &&
            (isNewPromptStatus) &&
            isRecentDateTime);
    }

    resolveIoxLogs(localLogs: IIdDictionary<DutyStatusLog>) {
        let currentActions = extend(true, {}, this.store.getState().data.ioxLogActions) as IIoxActionDictionary;
        let actionDatetimes = currentActions ? Object.keys(currentActions) : null;
        if (!actionDatetimes || !actionDatetimes.length || actionDatetimes.length === 0) {
            return;
        }
        for (let key in localLogs) {
            let log = localLogs[key];
            let logStatus = log.status;
            if (!log._isTempIoxLog && (logStatus === "ON" || logStatus === "OFF") && log.origin === "Automatic") {
                for (let i = 0; i < actionDatetimes.length; i++) {
                    let actionDateTime = actionDatetimes[i];
                    if (Math.abs(new Date(actionDateTime).getTime() - new Date(log.dateTime).getTime()) < constants.HOS.IOX_LOG_LENIENCY) {
                        let addManualDBeforeON = logStatus === "ON" && currentActions[actionDateTime] === IoxActionTypes.SET_AUTOON_TO_D;
                        let setAutoOffToManualPC = logStatus === "OFF" && currentActions[actionDateTime] === IoxActionTypes.SET_AUTOOFF_TO_PC;
                        if (addManualDBeforeON) {
                            let newLog = new DutyStatusLog({
                                id: idSerializer.get(),
                                dateTime: new Date(new Date(log.dateTime).getTime() - 1000).toISOString(),
                                status: "D",
                                device: log.device,
                                driver: log.driver,
                                origin: "Manual",
                                modified: "Add"
                            });
                            localLogs[newLog.id] = newLog;
                            this.deleteResolvedIoxAction(currentActions, actionDateTime);
                        } else if (setAutoOffToManualPC) {
                            log.status = "PC";
                            log.origin = "Manual";
                            log.modified = "Set";
                            this.deleteResolvedIoxAction(currentActions, actionDateTime);
                        }
                    }
                }
            }
        }
        this.automaticLog = null;
    }

    pushChangedToServer(localLogs: IIdDictionary<DutyStatusLog>) {
        this.resolveIoxLogs(localLogs);
        return new Promise<IIdDictionary<DutyStatusLog>>((resolve, reject) => {
            let calls: [IModifiedType, ISyncAddSet<IDutyStatusLog>][] = [];

            Object.keys(localLogs).forEach(logId => {
                let log = localLogs[logId];

                if (!log.id  && !log.placeholder) {
                    throw new Error("Attempting to sync a record without an ID");
                }

                if (log._isTempIoxLog) {
                    delete localLogs[log.id];
                } else {
                    if (!log.placeholder && log.modified && (log.modified === "Add" || log.modified === "Set" || log.modified === "Remove")) {
                        if (log._syncCounter === undefined) {
                            log._syncCounter = 5;
                        }
                        if (log._syncCounter > 0) {
                            // Log only retries (counter < 5), not first attempts, to avoid a row per pending record every cycle.
                            if (log._syncCounter < 5 && !this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_HOS_SYNC_TRACING)) {
                                logger.event(
                                    logger.Event.SYNCHOSPUSHRETRY,
                                    `SYNC_HOS_PUSH_RETRY logId=${ log.id } op=${ log.modified } attemptsRemaining=${ log._syncCounter }`
                                );
                            }
                            log._syncCounter--;
                            calls.push([log.modified, {
                                typeName: "DutyStatusLog",
                                entity: this.cleanLog(log)
                            }]);
                        } else {
                            logger.error(logger.Source.SYNC, `Failed to ${ log.modified } DutyStatusLog. Log ID: ${ log.id }`, this.currentUser);
                            delete localLogs[log.id];
                        }
                    }
                }
            });

            if (calls.length > 0) {
                logger.log(logger.Source.SYNC, common.formatCallForLogger("DutyStatusLogs", calls), this.currentUser);
                this.api.multiCall(calls, results => {
                    let getLocalEntity = (entity: IDutyStatusLog, _idFromServer: string | undefined, index: number) => {
                        let localEntity = localLogs[entity.id];
                        // localEntity can be missing if the record was deleted locally before this ack arrived (Sentry 7617129270).
                        if (!localEntity) {
                            if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_HOS_SYNC_TRACING)) {
                                logger.error(
                                    logger.Source.SYNC,
                                    `SYNC_HOS_ACK_UNRESOLVED localId=${ entity.id } op=${ calls[index][0] } position=${ index + 1 }/${ calls.length }`
                                );
                            }
                            return undefined;
                        }
                        this.syncLogger.toServer({
                            dateTime: localEntity.dateTime,
                            status: localEntity.status,
                            device: localEntity.device.id,
                            driver: localEntity.driver.id,
                            modified: localEntity.modified
                        });
                        return localEntity;
                    };
                    let removeLogs = (entity: IDutyStatusLog) => {
                        if (entity.status && (entity.status === "Connected" || entity.status === "Disconnected") || entity.modified === "Remove") {
                            // Delete connected/disconnected logs and removed logs once synced
                            delete localLogs[entity.id];
                        }
                    };

                    this.resolveSyncedEntities<IDutyStatusLog>(calls, results, getLocalEntity, removeLogs);
                    resolve(localLogs);
                // eslint-disable-next-line complexity
                }, errorObject => {
                    if (isOverLimitException(errorObject)) {
                        reject(errorObject);
                        return;
                    }
                    let requestIndex = errorObject.data.requestIndex;
                    let call = calls[requestIndex];
                    let log = call && call[1].entity;
                    let hasLogLocally = !!log && localLogs.hasOwnProperty(log.id);

                    if ((isDuplicateException(errorObject) && errorObject.message.indexOf("Two logs have the same id") > -1) || errorMessages.isLogAlreadySavedException(errorObject)) {
                        this.resolveAddConflicts(localLogs, log.id).then(resolve, reject);
                    } else if (errorMessages.isNotInSystemException(errorObject) && isRelationViolatedException(errorObject) && hasLogLocally) {
                        // TODO: For FB/134857 - we need to handle this with user input for "inactive-change-requested"
                        // For now we are deleting the local record and using the server as the official truth
                        delete localLogs[log.id];
                        this.pushChangedToServer(localLogs)
                            .then(resolve, reject);
                    } else if (errorMessages.isCoDriverRemovedException(errorObject) && isRelationViolatedException(errorObject) && hasLogLocally) {
                        this.resolveCoDriverRemoved(localLogs, log.id)
                            .then(modifiedLocalLogs => this.pushChangedToServer(modifiedLocalLogs))
                            .then(resolve, reject);
                    } else if (errorMessages.isInvalidAutomaticLog(errorObject)) {
                        this.deleteInvalidAutomaticLogs(localLogs);
                        resolve(localLogs);
                    } else if (errorMessages.isInvalidRuleSetChangeLog(errorObject)) {
                        this.deleteInvalidRuleSetRelatedLogs(localLogs);
                        resolve(localLogs);
                    } else if (errorMessages.isVehicleRemoved(errorObject)) {
                        let currentVehicle = getCurrentVehicle();
                        let removedVehicle = common.hasKnownDevice(log) ? log.device : null;

                        if (currentVehicle && removedVehicle && currentVehicle.id === removedVehicle.id) {
                            clearCurrentVehicle();
                            dispatcher.trigger(DispatcherEvents.VehicleRemoved);
                        }

                        if (removedVehicle) {
                            Object.keys(localLogs).forEach(logId => {
                                let localLog = localLogs[logId];

                                if (removedVehicle && common.hasKnownDevice(localLog) && (<any>localLog).device.id === removedVehicle.id) {
                                    delete localLogs[localLog.id];
                                }
                            });
                        }
                        this.pushChangedToServer(localLogs)
                            .then(resolve, reject);
                    } else if (errorObject.message === "HOS:Annotation log must have a timestamp.") {
                        let logIds = calls.map(singleCall => singleCall[1].entity.id);
                        let annotationCalls = logIds.reduce((annotationCallsArray: TMultiCallParam[], logId) => {
                            let annotations = localLogs[logId].annotations || [];

                            annotations.forEach(annotation => {
                                if (annotation.id && !annotation.hasOwnProperty("dateTime")) {
                                    annotationCallsArray.push(["Get", {
                                        typeName: "AnnotationLog",
                                        search: { id: annotation.id }
                                    }]);
                                }
                            });

                            return annotationCallsArray;
                        }, []);

                        this.api.multiCall(annotationCalls, data => {
                            let annotationIds: IAnnotation[] = data.reduce((ids, annotations) => {
                                if (annotations[0]) {
                                    ids[annotations[0].id] = true;
                                }
                                return ids;
                            }, {});

                            logIds.forEach(logId => {
                                let localLog = localLogs[logId];

                                if (localLog.annotations) {
                                    localLog.annotations = localLog.annotations.filter(a => a.hasOwnProperty("dateTime") || annotationIds[a.id]);
                                }
                            });

                            this.pushChangedToServer(localLogs)
                                .then(resolve, reject);
                        }, () => {
                            reject(errorObject);
                        });
                    } else if (hasLogLocally && localLogs[log.id].modified === "Add" && errorMessages.isExemptionPermissionException(errorObject)) {
                        // PC/YM permission was revoked while offline. Remove the failed log and any SDC logs that follow it.
                        logger.error(logger.Source.SYNC, `Exemption permission error for log ${ log.id }. Removing failed PC/YM log.`, this.currentUser);
                        const toastKey = log.status === "PC"
                            ? "Personal Conveyance is not allowed. Contact your fleet manager."
                            : "Yard Move is not allowed. Contact your fleet manager.";
                        void this.translator.translate(toastKey)
                            .then(translated => toastr.error(translated))
                            .catch(() => toastr.error(toastKey));
                        const failedLogDateTime = log.dateTime;
                        const failedLogDriverId = log.driver?.id;
                        delete localLogs[log.id];
                        // Remove an SDC log that directly follows the failed PC/YM log
                        if (failedLogDateTime && failedLogDriverId) {
                            // Find the datetime of the next exemption log after the failed one (if any)
                            const nextExemptionDateTime = Object.keys(localLogs)
                                .map(k => localLogs[k])
                                .filter(l => isSituationalDrivingStatus(l.status)
                                    && l.driver?.id === failedLogDriverId
                                    && l.dateTime && l.dateTime > failedLogDateTime)
                                .map(l => l.dateTime)
                                .sort()[0];

                            // Remove SDC logs between the failed log and the next exemption log (or all if no next exemption)
                            Object.keys(localLogs).forEach(logId => {
                                const localLog = localLogs[logId];
                                if (localLog.modified === "Add"
                                    && isSituationalDrivingClear(localLog.status)
                                    && localLog.driver?.id === failedLogDriverId
                                    && localLog.dateTime && localLog.dateTime > failedLogDateTime
                                    && (!nextExemptionDateTime || localLog.dateTime < nextExemptionDateTime)) {
                                    logger.log(logger.Source.SYNC, `Removing associated SDC log ${ logId } that follows failed PC/YM log.`, this.currentUser);
                                    delete localLogs[logId];
                                }
                            });
                        }
                        const driverContext = this.currentUser && this.getCachedContext(this.currentUser.name);
                        if (driverContext) {
                            driverContext.serverEvents.refreshCurrentUser(true);
                        }
                        this.pushChangedToServer(localLogs)
                            .then(resolve, reject);
                    } else if (hasLogLocally && (localLogs[log.id].modified === "Add" || errorMessages.isUnverifiedOrUnidentifiedDriverEditRequest(errorObject))) {
                        // Remove any failed "Add" or InvalidEditRequest calls from local.
                        handleApi(errorObject, true);
                        delete localLogs[log.id];
                        this.pushChangedToServer(localLogs)
                            .then(resolve, reject);
                    } else if (errorMessages.isCannotEditStatusLog(errorObject)) {
                        // Refresh version and odometer before retrying: a plain retry re-sends the
                        // stale values, fails identically, and burns _syncCounter until the log is
                        // silently deleted.
                        if (hasLogLocally) {
                            const logId = log.id;
                            this.api.call("Get", {
                                typeName: "DutyStatusLog",
                                search: { id: logId }
                            }, (serverLogs: IDutyStatusLog[]) => {
                                const serverLog = serverLogs && serverLogs[0];
                                const localLog = localLogs[logId];
                                if (serverLog && localLog) {
                                    localLog.version = serverLog.version;
                                    localLog.odometer = serverLog.odometer;
                                }
                                this.pushChangedToServer(localLogs).then(resolve, reject);
                            }, () => {
                                // Get failed (network or server error): retry unrefreshed, bounded by _syncCounter, if it
                                // exhausts, the log is dropped locally and the next full sync restores the server copy.
                                this.pushChangedToServer(localLogs).then(resolve, reject);
                            });
                        } else {
                            this.pushChangedToServer(localLogs).then(resolve, reject);
                        }
                    } else if (this.isInvalidEdit(errorObject) || errorMessages.isIgnoreHosLogClearanceException(errorObject) || errorMessages.isInvalidDutyStatusLogRemovalException(errorObject)) {
                        // Ended up editing a log in a way that is not allowed by the server. Notify error tracker and reset to server state. Don't publish a dialog.
                        logger.log(logger.Source.SYNC, `Error: ${ errorObject.message || "Error trying to modify a log." } | LogId: ${ log.id }`, this.currentUser);
                        this.resetInvalidEdit(localLogs, log)
                            .then(local => {
                                this.pushChangedToServer(local)
                                    .then(resolve, reject);
                            }, reject);
                    } else {
                        reject(errorObject);
                    }
                });
            } else {
                resolve(localLogs);
            }
        });
    }

    loadLogs(force = false, localLogsHash?: IIdDictionary<DutyStatusLog>) {
        let hosVersion = common.getFeedVersion(this.api, this.store, common.FeedTypeKey.HOS);
        let currUserId = this.currentUser ? this.currentUser.id : undefined;
        let removeBoundaryLogDate = () => currUserId && this.store.dispatch(DataActions.clearBoundaryLogDate(currUserId));
        let getSetBoundaryLogDate = (logs: DutyStatusLog[]) => {
            let maxDateBeforeRetentionMin: string | undefined;
            let storedBoundaryLogDate = currUserId && this.store.getState().data.boundaryLogDate[currUserId] || undefined;
            let retentionDate = this.doubleRetentionStartDate.toISOString();

            logs.forEach(log => {
                if (STANDARD_STATUSES.indexOf(log.status) > -1 && log.dateTime < retentionDate && (!maxDateBeforeRetentionMin || log.dateTime > maxDateBeforeRetentionMin)) {
                    maxDateBeforeRetentionMin = log.dateTime;
                }
            });

            // The retention date must have changed. This boundary log date is no longer usable.
            if (storedBoundaryLogDate && storedBoundaryLogDate > retentionDate) {
                removeBoundaryLogDate();
                storedBoundaryLogDate = undefined;
            }

            // Bump the retention date forward when possible
            if (maxDateBeforeRetentionMin && (!storedBoundaryLogDate || storedBoundaryLogDate < maxDateBeforeRetentionMin)) {
                this.store.dispatch(DataActions.setBoundaryLogDate({[currUserId as string]: maxDateBeforeRetentionMin}));
                return maxDateBeforeRetentionMin;
            }

            return storedBoundaryLogDate;
        };
        if (force
            || hosVersion === null
            || (this.firstCall && localLogsHash && Object.keys(localLogsHash).length === 0)) {
            removeBoundaryLogDate();

            // Full initial download with boundary logs.
            return new Promise<{logs: DutyStatusLog[]; version: string; isFullDownload: boolean}>((resolve, reject) => {
                this.callDutyStatusGetFeedWithResultLimit(
                    currUserId,
                    null,
                    STANDARD_STATUSES,
                    this.doubleRetentionStartDate,
                    this.doubleRetentionStartDate,
                    true,
                    null,
                    true,
                    false,
                    HOS_RESULTS_LIMIT,
                    "boundary download",
                    this.currentUser,
                    (feedLogs) => {
                        logger.log(logger.Source.SYNC, `Downloaded on init ${ feedLogs.length } boundary standard HOS records`, this.currentUser);
                        let boundaryDate = getSetBoundaryLogDate(feedLogs) || this.doubleRetentionStartDate;

                        // ToDate must be current datetime to allow all "Certify" logs to be downloaded. We rely on these logs to mark placeholder days as verified
                        return this.loadUserLogs(boundaryDate, undefined).then(data => {
                            resolve({...data, isFullDownload: true});
                        });
                    },
                    (err) => {
                        handleApi(err);
                        reject(err);
                    }
                );
            });
        } else {
            // Incremental sync
            return new Promise<{logs: DutyStatusLog[]; version: string; isFullDownload: boolean}>((resolve, reject) => {
                // boundary log date can be null under these conditions:
                // - the user has no logs yet.
                // - the min log date is later than the double retention date
                this.callDutyStatusGetFeedWithResultLimit(
                    currUserId,
                    null,
                    null,
                    null,
                    null,
                    false,
                    hosVersion,
                    true,
                    true,
                    HOS_RESULTS_LIMIT,
                    "incremental sync",
                    this.currentUser,
                    (rawFeedLogs, toVersion) => {
                        let boundaryDate = getSetBoundaryLogDate(rawFeedLogs);
                        let feedLogs = boundaryDate ? rawFeedLogs.filter(log => boundaryDate && log.dateTime >= boundaryDate) : rawFeedLogs;
                        logger.log(logger.Source.SYNC, `Downloaded with feed ${ feedLogs.length } HOS records`, this.currentUser);
                        this.driverVersion = toVersion;
                        resolve({logs: feedLogs, version: toVersion, isFullDownload: false});
                    },
                    (err) => {
                        handleApi(err);
                        reject(err);
                    }
                );
            });
        }
    }

    loadUserLogs(fromDate: Date | string, toDate: Date | string | undefined) {
        return new Promise<{logs: DutyStatusLog[]; version: string}>((resolve, reject) => {
            let currUserId = this.currentUser ? this.currentUser.id : null;
            this.callDutyStatusGetFeedWithResultLimit(
                currUserId,
                null,
                null,
                fromDate,
                toDate || null,
                false,
                null,
                true,
                true,
                HOS_RESULTS_LIMIT,
                "load user logs",
                this.currentUser,
                (controlLogs, toVersion) => {
                    logger.log(logger.Source.SYNC, `Downloaded on init ${ controlLogs.length } HOS records`, this.currentUser);
                    this.driverVersion = toVersion;
                    resolve({logs: controlLogs, version: toVersion});
                },
                (err) => {
                    handleApi(err);
                    reject(err);
                }
            );
        });
    }

    fetchData(postSyncCallback: (e?: Error) => void, force: boolean, syncSource: TSyncSource = "user") {
        this.verifyIfPreviousSyncWasCompleted("HOS");

        this.currentUser = this.api.getUser();
        this.newLogs = [];
        let versions: IIdDictionary<string> = {};
        const SYNCED_HOS_FEED_KEY = "globalHosFeedVersion";
        const SYNCED_MALFUNCTION_FEED_KEY = "globalMalfunctionFeedVersion";
        let localPostSyncCallback = postSyncCallback || NOOP;
        let localLogsHash: IIdDictionary<DutyStatusLog> = this.getLogs() || {};
        let serverOrClientHasNewLogs = Object.keys(localLogsHash).some(key => !!localLogsHash[key].modified || !!localLogsHash[key].previousState);
        let saveData = (localLogs: IIdDictionary<DutyStatusLog>) => {
            if (serverOrClientHasNewLogs || this.firstCall) {
                this.setLogs(localLogs);
                this.firstCall = false;
            }

            common.setFeedVersion(this.api, this.store, common.FeedTypeKey.HOS, versions[SYNCED_HOS_FEED_KEY] || "", undefined, this.syncLogger);
            common.setFeedVersion(this.api, this.store, common.FeedTypeKey.MALFUNCTION, versions[SYNCED_MALFUNCTION_FEED_KEY] || "", undefined, this.syncLogger);

            this.syncLogger.setFeedVersion(SYNCED_HOS_FEED_KEY, this.getDriverVersion() || "");
            this.syncLogger.setFeedVersion(SYNCED_MALFUNCTION_FEED_KEY, this.malfunctionsHelper.malfunctionFeedVersion || "");

            this.syncAnnotationLogs([]);
        };
        let handleSyncSuccess = (localLogs: IIdDictionary<DutyStatusLog>) => {
            saveData(localLogs);
            this.updateRunStateAtSyncComplete();
            dispatcher.trigger(DispatcherEvents.SyncHos, this.currentUser?.id);

            // Only trigger AFTER sync is done and logs are saved
            if (this.automaticLog) {
                dispatcher.trigger(DispatcherEvents.VehicleEndedStatus, this.automaticLog.status === "D" ? undefined : this.automaticLog);
                this.automaticLog = null;
            }

            // Update bell notification for uncertified HOS logs after sync
            if (this.currentUser?.id) {
                let userId = this.currentUser.id;
                // isLogout=false: notification only covers previous days' logs, not today's (per ticket requirements)
                let hasUncertifiedLogs = this.dutyStatusLogService.isAnyLogUnverifiedForWorkflow(this.hosProcess, false, this.currentUser);
                let dismissedDate = this.store.getState().openCabWorkflow.userLastSeenUncertifiedHosNotificationDate?.[userId];
                let isDismissedRecently = isNotificationDismissedRecently(dismissedDate, getServerDate());
                logger.log(logger.Source.SYNC, `Checked for uncertified HOS logs | uncertified logs: ${ hasUncertifiedLogs }`, this.currentUser);

                if (!hasUncertifiedLogs) {
                    Notifications.remove("uncertifiedHosLogs", userId);
                    this.store.dispatch(OpenCabWorkflowActions.clearUserLastSeenUncertifiedHosNotificationDate(userId));
                } else if (!isDismissedRecently) {
                    this.translator.translate(UNCERTIFIED_HOS_NOTIFICATION_STRING)
                        .then(translatedMessage => {
                            if (this.currentUser && this.dutyStatusLogService.isAnyLogUnverifiedForWorkflow(this.hosProcess, false, this.currentUser)) {
                                Notifications.addUncertifiedHosLogsNotification(userId, this.currentUser, translatedMessage, () => {
                                    this.store.dispatch(OpenCabWorkflowActions.setUserLastSeenUncertifiedHosNotificationDate(userId, getServerDate().toISOString()));
                                });
                            }
                        })
                        .catch(error => handleApi(error));
                }
            }

            logger.log(logger.Source.SYNCDIGEST, this.syncLogger.getLogs(), this.currentUser);
            this.syncLogger.setSource(syncSource);
            localPostSyncCallback();
        };
        let handleSyncError = error => {
            this.updateRunStateAtSyncComplete(error);
            this.syncLogger.setSource(syncSource);
            localPostSyncCallback(error);
        };

        // If currentUser is not set somehow ... just resolve with local logs and back out
        if (!this.currentUser || !this.currentUser.id || this.currentUser.id === NO_USER_ID) {
            return Promise.resolve(this.syncDutyStatusLogs([] as DutyStatusLog[], localLogsHash, force))
            .then(handleSyncSuccess)
            .catch(handleSyncError);
        }
        let wasFullDownload = false;
        return Promise.all([
            this.loadLogs(force, localLogsHash),
            this.loadUnidentifiedLogs(force)
        ])
        .then(results => {
            // sort server logs
            wasFullDownload = results[0]?.isFullDownload ?? false;
            let logs = (results[0] && results[0].logs || [])
                .concat(results[1] || [])
                .sort(dateTimeComparator());
            versions[SYNCED_HOS_FEED_KEY] = results[0] && results[0].version || "";

            if (!serverOrClientHasNewLogs) {
                serverOrClientHasNewLogs = logs.length > 0;
            }

            if (this.debugFail) {
                throw new Error("Debug");
            }

            this.newLogs = this.newLogs.concat(logs);
            return this.syncDutyStatusLogs(logs, localLogsHash, force);
        })
        .then(localLogs => {
            if (Object.keys(localLogs).length && getCurrentVehicle()) {
                return this.malfunctionsHelper.processMalfunctions(localLogs, force)
                    .then(malfunctionData => {
                        if (malfunctionData.logs && malfunctionData.logs.length > 0) {
                            serverOrClientHasNewLogs = true;
                            this.newLogs = this.newLogs.concat(malfunctionData.logs);
                        }
                        versions[SYNCED_MALFUNCTION_FEED_KEY] = malfunctionData.version;
                        return this.syncDutyStatusLogs(malfunctionData.logs, localLogs, undefined, true);
                    })
                    .catch(e => {
                        // Mimics the behaviour above as if `malfunctionLogs` is empty, allowing the sync to proceed if the call fails or times out
                        handleApi(e);
                        logger.error(logger.Source.SYNC, `Malfunction sync failed, continuing HOS sync with empty malfunction logs`, this.currentUser, e);
                        return this.syncDutyStatusLogs([], localLogs, undefined, true);
                    });
            }
            return localLogs;
        })
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.getLogLocations(localLogs))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.handlePositionMalfunctions(localLogs, this.currentUser))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.cleanIncorrectMalfunctionTypes(localLogs))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.reassignLogs(localLogs))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.updateInTransitionLogs(localLogs))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.resolvePcYmEdits(localLogs))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.resolveRequestedEdits(localLogs))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.pushChangedToServer(localLogs))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.loadAnnotationsByLogs(this.newLogs, localLogs, force))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.cleanUnassignedLogs(localLogs, this.currentUser))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => {
            // Prune on first incremental sync only. Skip on any full download (force sync or empty-hash
            // re-download) since those already fetch only within the retention window, and skip on
            // subsequent syncs within the same session to avoid repeated pruning overhead.
            return this.firstCall && !wasFullDownload ? this.pruneLogsToRetentionWindow(localLogs) : localLogs;
        })
        .then((localLogs: IIdDictionary<DutyStatusLog>) => this.preloadData(localLogs))
        .then((localLogs: IIdDictionary<DutyStatusLog>) => {
            let lengthBeforePlaceholders = Object.keys(localLogs).length;
            let logsWithPlaceholders = this.addPlaceholders(localLogs, force);

            if (Object.keys(logsWithPlaceholders).length !== lengthBeforePlaceholders) {
                serverOrClientHasNewLogs = true;
            }

            if (serverOrClientHasNewLogs) {
                return this.odometerData.fetchAndPopulateOdometerData(this.doubleRetentionStartDate, logsWithPlaceholders)
                    .catch(error => {
                        handleApi(error, true);
                        return Promise.resolve(logsWithPlaceholders);
                    });
            }
            return logsWithPlaceholders;
        })
        .then((localLogs: IIdDictionary<DutyStatusLog>) => {
            return serverOrClientHasNewLogs ? this.preloadStatusData(localLogs) : localLogs;
        })
        .then(handleSyncSuccess)
        .catch(handleSyncError);
    }

    getLogsByIdFromListMulticall(calls: TMultiCallParam[]): Promise<IDutyStatusLog[][]> {
        return new Promise(resolve => {
            if (!calls.length) {
                resolve([]);
                return;
            }

            this.api.multiCall(calls, resolve, () => resolve([]));
        });
    }

    getLogsByIdFromList(
        listOfIds: string[],
        localLogs: IIdDictionary<DutyStatusLog>,
        logAction: (ids: string[], localLogs: IIdDictionary<DutyStatusLog>, logsFromServer: IDutyStatusLog[][]) => void
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!listOfIds.length) {
                resolve();
                return;
            }
            const clonedListOfIds = [...listOfIds];

            let calls: TMultiCallParam[] = listOfIds.map(id => ["Get", {
                typeName: "DutyStatusLog",
                search: { id, includeModifications: true }
            }]);

            // Possibility of hitting OverLimitException if there are lots of logs so reduce the multicall count size
            const chunkedCalls: TMultiCallParam[][] = [];
            const chunkedIds: string[][] = [];
            for (let i = 0; i < calls.length; i += LOG_ID_LOOKUP_CHUNK_SIZE) {
                chunkedCalls.push(calls.slice(i, i + LOG_ID_LOOKUP_CHUNK_SIZE));
                chunkedIds.push(clonedListOfIds.slice(i, i + LOG_ID_LOOKUP_CHUNK_SIZE));
            }
            Promise.all(chunkedCalls.map((chunk, index) => {
                return this.getLogsByIdFromListMulticall(chunk).then(logsFromServer => {
                    logAction(chunkedIds[index], localLogs, logsFromServer);
                });
            }))
                .then(() => resolve())
                .catch(reject);
        });
    }

    updateInTransitionLogs(localLogs: IIdDictionary<DutyStatusLog>) {
        return new Promise(resolve => {
            let inTransitionLogIds: string[] = [];
            Object.keys(localLogs).forEach(logId => {
                let log = localLogs[logId];
                if (!log.placeholder && log._awaitingTransition) {
                    inTransitionLogIds.push(logId);
                }
            });

            if (inTransitionLogIds.length) {
                this.getLogsByIdFromList(inTransitionLogIds, localLogs, (ids, logDict, logsFromServer) => {
                    logsFromServer.forEach((logs, index) => {
                        let convertedLogs = this.dutyStatusLogService.convertDutyStatusLogsFromServer(logs, this.currentUser);
                        let id = ids[index];
                        if (convertedLogs.length) {
                            let log = convertedLogs[0];
                            if (log.state === "Inactive" ||
                                (this.currentUser && log.driver.id !== this.currentUser.id && log.requestedEdit?.driver.id !== this.currentUser.id)) {
                                    delete logDict[id];
                            }
                        } else {
                            // Log no longer visible, assume it was moved to other user
                            delete logDict[id];
                        }
                    });
                }).then(() => {
                    this.setLogs(localLogs);
                    resolve(localLogs);
                }).catch(errorObject => {
                    handleApi(errorObject, true);
                    resolve(localLogs);
                });
            } else {
                resolve(localLogs);
            }
        });
    }

    addPlaceholders(localLogs: IIdDictionary<DutyStatusLog>, force: boolean): IIdDictionary<DutyStatusLog> {
        let now = getServerDate();
        let getLastLog = driverLogsArray => {
            let lastDriverLog = driverLogsArray[driverLogsArray.length - 1];

            return extend({}, lastDriverLog ? lastDriverLog  : {}, {
                dateTime: now,
                placeholder: true
            });
        };
        let canBePlaceholder = (log: DutyStatusLog): boolean => {
            return log && !log.placeholder && log.driver.id !== NO_USER_ID && STANDARD_STATUSES.indexOf(log.status) > -1;
        };
        let driverLogs = Object.keys(localLogs)
            .map(logId => localLogs[logId])
            .filter(canBePlaceholder)
            .sort(dateTimeComparator());

        driverLogs = removeHiddenRequestedAdds(driverLogs);
        driverLogs.sort(dateTimeComparator());

        let certificationLogs = Object.keys(localLogs)
            .map(logId => localLogs[logId])
            .filter(l => l.status === "Certify");

        let firstLogDateTime = driverLogs.length ? new Date(driverLogs[0].dateTime) : getServerDate();
        let logsWithPlaceholders = Object.keys(localLogs).reduce<IIdDictionary<DutyStatusLog>>((res, logId: string) => {
            let log = localLogs[logId];

            if (log.placeholder) {
                if (new Date(log.dateTime).getTime() >= firstLogDateTime.getTime() && !force) {
                    delete log.verifyDateTime;
                    res[logId] = log;
                }
            } else {
                res[logId] = log;
            }

            return res;
        }, {} as any);
        let newPlaceholders = {};
        let addPlaceholderLogs = (log: DutyStatusLog, nextLog: DutyStatusLog, certLogs: DutyStatusLog[]) => {
            let thisDate: Date;
            let nextDate: Date;
            let placeholderDateTime: Date;

            if (log) {
                thisDate = new Date(log.dateTime);
                nextDate = new Date((nextLog || log).dateTime);
                placeholderDateTime = getNextDayStart(thisDate);

                while (placeholderDateTime < nextDate) {
                    if (placeholderDateTime.getTime() >= this.doubleRetentionStartDate.getTime() && placeholderDateTime.getTime() <= now.getTime()) {
                        //Get odometer at start of day overlap
                        const placeholderLog = logsWithPlaceholders[placeholderDateTime.valueOf()] || {
                            id: placeholderDateTime.valueOf(),
                            placeholder: true,
                            dateTime: placeholderDateTime.toISOString(),
                            parentId: log.id
                        };
                        this.updatePlaceholderProperties(new DutyStatusLog(log), new DutyStatusLog(placeholderLog), newPlaceholders, placeholderDateTime, certLogs);
                    }

                    let newPlaceholderDateTime = getNextDayStart(placeholderDateTime, true);

                    // if for some reason resulted next day start is equal or less then previous value then break the cycle to prevent infinite loop
                    if (newPlaceholderDateTime.getTime() <= placeholderDateTime.getTime()) {
                        logger.error(logger.Source.SYNC, `Prevented infinite loop. Incorrect next placeholder date calculation after ${ placeholderDateTime.toISOString() }`, this.currentUser);
                        break;
                    }
                    placeholderDateTime = newPlaceholderDateTime;
                }
            }
        };
        let lastLog = getLastLog(driverLogs);

        for (let i = 0; i < driverLogs.length; i++) {
            let log = driverLogs[i];
            let nextLog = (i < driverLogs.length - 1) ? driverLogs[i + 1] : lastLog;

            addPlaceholderLogs(log, nextLog, certificationLogs);
        }

        // filter out placeholder logs that are unnecessary
        Object.keys(logsWithPlaceholders).forEach(id => {
            if (logsWithPlaceholders[id].placeholder) {
                delete logsWithPlaceholders[id];
            }
        });

        return { ...logsWithPlaceholders, ...newPlaceholders };
    }

    updatePlaceholderProperties(log: DutyStatusLog, placeholderLog: DutyStatusLog, logsWithPlaceholders: IIdDictionary<DutyStatusLog>, placeholderDateTime: Date, certLogs: DutyStatusLog[]) {
        if (log.id !== placeholderLog.parentId) {
            placeholderLog.parentId = log.id;
        }

        placeholderLog.status = log.status;
        placeholderLog.device = log.device;
        placeholderLog.driver = log.driver;
        placeholderLog.coDrivers = log.coDrivers;
        placeholderLog.isIgnored = log.isIgnored;
        placeholderLog._awaitingTransition = log._awaitingTransition;

        logsWithPlaceholders[placeholderDateTime.valueOf()] = placeholderLog;

        // If at least one of the certify logs for this day was made after the log's last editDateTime,
        // AND the log does not have an active requested edit, set the placeholder as verified.
        if (isPlaceholderCertified(log, placeholderDateTime, certLogs)) {
            placeholderLog.verifyDateTime = placeholderDateTime.toISOString();
        }
    }

    synchronize(progressCb: common.TSyncCallback, force: boolean, syncSource?: TSyncSource, profilingBatcher?: PerformanceProfileBatcher): Promise {
        return this.createProfiledSync(MeasureName.SyncHos, () => {
            // Last sync might have failed due to rate limiting. Don't repeat those calls
            if (!(window.DRIVE_DEBUG && window.DRIVE_DEBUG?.DISABLE_SYNC_FAILURE_DELAY) && this.didSyncFailRecently()) {
                logger.event(logger.Event.SYNCHOSCONFLICT, "Skip HOS sync because previous run failed very recently");
                progressCb("HOS");
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                this.fetchData((errorObject?: Error) => {
                    progressCb("HOS", errorObject, this.syncLogger);
                    resolve();
                }, force, syncSource);
            });
        }, profilingBatcher);
    }

    getDriverVersion() {
        return this.driverVersion;
    }

    checkValue(obj: DutyStatusLog): void {
        if (!obj.dateTime) {
            throw new Error("Log must have dateTime");
        }

        if (!obj.driver) {
            throw new Error("Log must have driver");
        }

        if (!obj.device) {
            throw new Error("Log must have device");
        }

        if (!obj.status) {
            throw new Error("Log must have status");
        }

        switch (obj.status) {
            case "PowerCompliance":
            case "EngineSyncCompliance":
            case "TimingCompliance":
            case "PositioningCompliance":
            case "DataRecordingCompliance":
            case "DataTransferCompliance":
            case "OtherCompliance":
            case "MissingElementCompliance":
            case "UnidentifiedDrivingCompliance": {
                if (obj.status === "MissingElementCompliance") {
                    if (obj.malfunction === "None") {
                        throw new Error(`Log with ${ obj.status } statuses cannot be set to Malfunction "None"`);
                    }
                    break;
                }
                if (obj.malfunction === "None") {
                    throw new Error(`Log with ${ obj.status } "statuses" cannot be set to Malfunction "None"`);
                }
                if (obj.driver.id !== "NoUserId") {
                    throw new Error(`Log with ${ obj.status } "must" be assigned to NoUser.`);
                }
                break;
            }
            case "Certify":
                if (!obj.verifyDateTime) {
                    throw new Error(`Certify log must have a VerifyDateTime (representing the the date of the logs being certified).`);
                }
                break;
            default:
                break;
        }
    }

    private getLogs() {
        let userId = this.currentUser ? this.currentUser.id : undefined;
        let hosLogs = this.dutyStatusLogService.getUserLogsDictionary(userId);
        let hosSystemLogs = this.dutyStatusLogService.getSystemLogsDictionary(userId);
        return extend(hosLogs, hosSystemLogs);
    }

    private setLogs(logsToSave: IIdDictionary<DutyStatusLog>) {
        const hosLogs: IIdDictionary<DutyStatusLog> = {};
        const userId = this.currentUser ? this.currentUser.id : undefined;

        if (!userId){
            return;
        }

        let hosSystemLogs: IIdDictionary<DutyStatusLog> = {};

        for (const key in logsToSave) {
            const currentLog = logsToSave[key];
            if (DISPLAY_STATUSES.indexOf(currentLog.status) !== -1) {
                hosLogs[currentLog.id] = currentLog;
            } else {
                hosSystemLogs[currentLog.id] = currentLog;
            }
        }
        const numberOfUserLogs = Object.keys(hosLogs).length;
        if (numberOfUserLogs > constants.ALLOWED_UNCOMPRESSED_HOSLOGS_AMOUNT) {
            logger.event(
                logger.Event.EXCESSIVEDUTYSTATUSLOGS,
                `${ "Excessive user HOS logs: " + numberOfUserLogs + " since: " + this.doubleRetentionStartDate.toISOString() }`,
                this.currentUser
            );
        }
        const numberOfSystemLogs = Object.keys(hosSystemLogs).length;
        if (numberOfSystemLogs > constants.ALLOWED_UNCOMPRESSED_HOSLOGS_AMOUNT) {
            logger.event(
                logger.Event.EXCESSIVEDUTYSTATUSLOGS,
                `${ "Excessive system HOS logs: " + numberOfSystemLogs + " since: " + this.doubleRetentionStartDate.toISOString() }`,
                this.currentUser
            );
        }
        if (numberOfSystemLogs > constants.SYSTEM_HOS_LOGS_MAX_SAVED_COUNT) {
            hosSystemLogs = this.handleExcessiveHosLogs(hosSystemLogs);
        }
        this.store.dispatch(HosLogActions.setSystemHosLogs({ userId: userId, logs: hosSystemLogs }));
        this.store.dispatch(HosLogActions.setUserHosLogs({ userId: userId, logs: hosLogs }));
    }

    private handleExcessiveHosLogs(hosLogs: IIdDictionary<DutyStatusLog>): IIdDictionary<DutyStatusLog> {
        const { modifiedHosSystemLogs, unmodifiedHosSystemLogs } = this.partitionDutyStatusLogsByModification(hosLogs);

        const modifiedHosSystemLogsCount = Object.keys(modifiedHosSystemLogs).length;
        if (modifiedHosSystemLogsCount >= constants.SYSTEM_HOS_LOGS_MAX_SAVED_COUNT) {
            return this.getLatestDutyStatusLogs(modifiedHosSystemLogs, constants.SYSTEM_HOS_LOGS_MAX_SAVED_COUNT);
        }
        const remainingMaxCount = constants.SYSTEM_HOS_LOGS_MAX_SAVED_COUNT - modifiedHosSystemLogsCount;
        const latestUnmodifiedHosSystemLogs = this.getLatestDutyStatusLogs(unmodifiedHosSystemLogs, remainingMaxCount);
        return extend(latestUnmodifiedHosSystemLogs, modifiedHosSystemLogs);
    }

    private partitionDutyStatusLogsByModification(hosSystemLogs: IIdDictionary<DutyStatusLog>): {
        modifiedHosSystemLogs: IIdDictionary<DutyStatusLog>; unmodifiedHosSystemLogs: IIdDictionary<DutyStatusLog>;
    } {
        const modifiedHosSystemLogs: IIdDictionary<DutyStatusLog> = {};
        const unmodifiedHosSystemLogs: IIdDictionary<DutyStatusLog> = {};

        Object.keys(hosSystemLogs).forEach(key => {
            const log = hosSystemLogs[key];
            if (log.modified) {
                modifiedHosSystemLogs[log.id] = log;
            } else {
                unmodifiedHosSystemLogs[log.id] = log;
            }
        });

        return { modifiedHosSystemLogs, unmodifiedHosSystemLogs };
    }

    private pruneLogsToRetentionWindow(localLogs: IIdDictionary<DutyStatusLog>): IIdDictionary<DutyStatusLog> {
        if (Object.keys(localLogs).length < constants.MIN_LOGS_FOR_PRUNING) {
            return localLogs;
        }

        const retentionStart = this.doubleRetentionStartDate.getTime();
        let boundaryLog: DutyStatusLog | undefined;

        // First pass: find the most recent pre-window STANDARD_STATUSES log (the boundary anchor)
        Object.keys(localLogs).forEach(key => {
            const log = localLogs[key];
            if (new Date(log.dateTime).getTime() < retentionStart &&
                STANDARD_STATUSES.indexOf(log.status) > -1 &&
                (!boundaryLog || new Date(log.dateTime).getTime() > new Date(boundaryLog.dateTime).getTime())) {
                boundaryLog = log;
            }
        });

        const cutoffTime = boundaryLog ? new Date(boundaryLog.dateTime).getTime() : retentionStart;
        const prunedLogs: IIdDictionary<DutyStatusLog> = {};

        // PositioningCompliance logs can exist outside of the retention period and have a special boundary download.
        // Need to re-evaluate if they can be pruned in a future case.
        // To keep behaviour consistent, they will not be pruned for now.
        Object.keys(localLogs).forEach(key => {
            const log = localLogs[key];
            if (log.status === "PositioningCompliance" || new Date(log.dateTime).getTime() >= cutoffTime) {
                prunedLogs[key] = log;
            }
        });

        const prunedCount = Object.keys(localLogs).length - Object.keys(prunedLogs).length;
        if (prunedCount > 0) {
            logger.event(
                logger.Event.HOSLOGRETENTIONPRUNED,
                `Pruned ${ prunedCount } HOS logs with cutoff: ${ new Date(cutoffTime).toISOString() }`,
                this.currentUser
            );
        }

        if (boundaryLog) {
            const currUserId = this.currentUser?.id;
            if (currUserId) {
                const storedBoundaryDate = this.store.getState().data.boundaryLogDate[currUserId];
                if (!storedBoundaryDate || new Date(boundaryLog.dateTime) > new Date(storedBoundaryDate)) {
                    this.store.dispatch(DataActions.setBoundaryLogDate({ [currUserId]: boundaryLog.dateTime }));
                }
            }
        }

        return prunedLogs;
    }

    private getLatestDutyStatusLogs(logs: IIdDictionary<DutyStatusLog>, maxCount: number): IIdDictionary<DutyStatusLog> {
        if (!logs || maxCount <= 0) {
            return {};
        }

        return Object.keys(logs)
            .map(key => logs[key])
            .sort(dateTimeComparator(false))
            .slice(0, maxCount)
            .reduce((acc, log) => {
                acc[log.id] = log;
                return acc;
            }, {} as IIdDictionary<DutyStatusLog>);
    }

    private getLogId(log: IDutyStatusLog) {
        return log.parentId || log.id;
    }

    private isMainExemptionLogEdit(call: [IModifiedType, ISyncAddSet<IDutyStatusLog>]): boolean {
        const entity = call[1].entity;
        return (["PC", "YM"].indexOf(entity.status) > -1 && entity.isRequestedRemoval) || (entity.status === SITUATIONAL_DRIVING_CLEAR && call[0] === "Set");
    }

    private pushRequestedChangesToServer(
        calls: [IModifiedType, ISyncAddSet<IDutyStatusLog>][],
        allResolvedRequests: DutyStatusLog[],
        localLogs: IIdDictionary<DutyStatusLog>
    ) {
        return new Promise((resolve, reject) => {
            let handleSuccess = () => {
                for (let resolvedRequest of allResolvedRequests) {
                    let id = <string>resolvedRequest.parentId;
                    let log = localLogs[id];

                    if (log && !(resolvedRequest.state === "Active" && this.currentUser && resolvedRequest.driver.id !== this.currentUser.id)) {
                        this.cleanRequestedLog(log);
                    }
                }
                resolve(localLogs);
            };

            if (calls.length === 0) {
                handleSuccess();
                return;
            }

            logger.log(logger.Source.SYNC, common.formatCallForLogger("DutyStatusLogRequest", calls), this.currentUser);
            this.api.multiCall(calls, handleSuccess, errorObject => {
                if (isOverLimitException(errorObject)) {
                    reject(errorObject);
                    return;
                }

                let requestIndex = errorObject?.data?.requestIndex;
                let call = requestIndex !== undefined && calls[requestIndex];
                let failedLog = call && call[1].entity;

                if (!failedLog) {
                    reject(errorObject);
                    return;
                }

                let logId = this.getLogId(failedLog);
                if (errorMessages.isNotInSystemException(errorObject) && isRelationViolatedException(errorObject) && localLogs.hasOwnProperty(logId)) {
                    // Log was deleted. Keep trying to sync remaining logs up to server
                    delete localLogs[logId];
                    let newCalls = calls.slice(requestIndex + 1);
                    this.pushRequestedChangesToServer(newCalls, allResolvedRequests, localLogs).then(resolve, reject);
                } else if (this.isInvalidEdit(errorObject)) {
                    logger.log(logger.Source.SYNC, `Error: trying to edit log with a pending edit request, logId: ${ failedLog.id }`, this.currentUser);
                    this.resetInvalidEdit(localLogs, failedLog)
                        .then(() => {
                            let newCalls = calls.slice(requestIndex + 1);
                            return this.pushRequestedChangesToServer(newCalls, allResolvedRequests, localLogs);
                        })
                        .then(resolve, reject);
                } else {
                    // Don't know what this is
                    reject(errorObject);
                }
            });
        });
    }

    private isReviewedCarrierAddEdit(log) {
        return (log.requestedAdd && this.dutyStatusLogService.isReviewed(log)) || (!!log.requestedEdit && this.dutyStatusLogService.isReviewed(log.requestedEdit));
    }

    private cleanRequestedLog(log) {
        delete log.requestedEdit;
        delete log.requestedAdd;
        delete log.originalDriverBeforeEdit;
        delete log._originalDateTimeBeforeEdit;
    }

    private cleanLog(log: DutyStatusLog) {
        let sanitizedLog: IDutyStatusLog = extend({}, log);
        this.removeClientSideProperties(sanitizedLog);

        delete sanitizedLog.placeholder;
        delete sanitizedLog.distance;
        delete sanitizedLog.requestedEdit;
        delete sanitizedLog.requestedAdd;
        delete sanitizedLog.engineHours;
        delete sanitizedLog.originalDriverBeforeEdit;

        if (this.store.getState().data.ELDJurisdiction !== "EWD" && sanitizedLog.status !== "Work" && sanitizedLog.status !== "Rest" && sanitizedLog.status !== "WorkExemption") {
            delete sanitizedLog.odometer;
        }

        // Clean out AnnotationLogs
        (sanitizedLog.annotations || []).forEach(annotation => this.removeClientSideProperties(annotation));

        // Clear out location & distances before pushing to server
        let isAppropriateMalfunction = sanitizedLog.malfunction !== "DiagnosticManualPosition"
                                    && sanitizedLog.malfunction !== "MalfunctionManualPosition"
                                    && sanitizedLog.malfunction !== "ManualPosition";

        if (sanitizedLog.malfunction !== "None"
            && MALFUNCTION_STATUSES.indexOf(sanitizedLog.status) === -1
            && isAppropriateMalfunction) {
            delete sanitizedLog.malfunction;
        }

        if (sanitizedLog.device && sanitizedLog.device.id !== NO_DEVICE_ID && (!sanitizedLog.malfunction || isAppropriateMalfunction)) {
            delete sanitizedLog.location;
        }

        if (!common.hasKnownDevice(sanitizedLog)) {
            sanitizedLog.device = { id: "NoDeviceId" };
        } else {
            sanitizedLog.device = {id: sanitizedLog.device.id};
        }

        sanitizedLog.driver = {id: sanitizedLog.driver.id};
        return sanitizedLog;
    }

    // remove the unassigned logs that does not belong to the current vehicle
    private cleanUnassignedLogs(logs: IIdDictionary<DutyStatusLog>, currentUser: IUser | null): IIdDictionary<DutyStatusLog> {
        let currentVehicle: TVehicle = getCurrentVehicle();
        let previousVehicle: TVehicle = getPreviousVehicle();
        if (currentUser && currentVehicle && (!previousVehicle || currentVehicle.id !== previousVehicle.id)) {
            setPreviousVehicle({ id: currentVehicle.id });

            let newLogs = logs ? extend({}, logs) : {};
            let isDriversCarrierEdit = (log: DutyStatusLog) => {
                return log.driver.id === NO_USER_ID && !!log.requestedEdit && !!log.requestedEdit.driver && log.requestedEdit.driver.id === currentUser.id;
            };

            Object.keys(logs).forEach(key => {
                let log = logs[key];

                if (log.driver.id === NO_USER_ID && currentVehicle && (log.device && log.device.id && log.device.id !== currentVehicle.id) && !isDriversCarrierEdit(log)) {
                    delete newLogs[key];
                }
            });

            return newLogs;
        } else {
            return logs;
        }
    }

    // preloads vehicles, attachments, addresses to have them locally
    private preloadData(localLogs: IIdDictionary<DutyStatusLog>): Promise<IIdDictionary<DutyStatusLog>> {
        let currentUser = this.currentUser;
        let activeVehicles = {};
        let trailerAttachmentDateLookup = {};

        // Prepare local logs for push to server
        Object.keys(localLogs).forEach((logId: any) => {
            let log = localLogs[logId];

            if (!log.id) {
                throw new Error("Attempting to sync a record without an ID");
            }

            if (log.modified || log.fromServer) {
                if (!common.hasKnownDevice(log)) {
                    log.device = { id: NO_DEVICE_ID };
                } else {
                    activeVehicles[log.device.id] = log.device;
                }

                if (log.driver.id !== NO_USER_ID) {
                    // Find logs that need a trailer lookup
                    if (common.hasKnownDevice(log)) {
                        let deviceId = log.device.id;

                        if (new Date(log.dateTime).getTime() >= this.doubleRetentionStartDate.getTime()) {
                            trailerAttachmentDateLookup[deviceId] = trailerAttachmentDateLookup[deviceId] || [];
                            trailerAttachmentDateLookup[deviceId].push(new Date(log.dateTime));
                        }
                    }
                }
            }

            delete log.fromServer;
        });

        return Promise.all([
            common.preloadVehicles(activeVehicles, currentUser, this.api, this.store),
            this.preloadAttachments(trailerAttachmentDateLookup, currentUser)
        ]).then(() => localLogs);
    }

    private preloadAttachments(attachments, currentUser) {
        let apiUserId = currentUser ? currentUser.id : null;
        let attachmentLookups: any[] = [];
        let deviceId;
        let trailerLookupLogsDates;
        let minDate;
        let maxDate;

        if (this.securityService.can("viewTrailers", apiUserId)) {
            for (deviceId in attachments) {
                if (attachments.hasOwnProperty(deviceId)) {
                    trailerLookupLogsDates = attachments[deviceId];
                    minDate = new Date(Math.min.apply(null, trailerLookupLogsDates));
                    maxDate = new Date(Math.max.apply(null, trailerLookupLogsDates));

                    attachmentLookups.push({
                        deviceSearch: {
                            id: deviceId
                        },
                        activeFrom: minDate,
                        activeTo: maxDate
                    });
                }
            }
            return this.fetchTrailerAttachments(attachmentLookups, apiUserId)
            .then(fetchedAttachments => {
                let trailerIds = fetchedAttachments.reduce((ids: string[], attachment) => {
                    let trailer = attachment && attachment.trailer;
                    trailer && ids.indexOf(trailer.id) === -1 && ids.push(trailer.id);
                    return ids;
                }, []);

                return trailerIds.length ? this.trailerCache.getOrFetch(this.api, trailerIds.map(id => ({ id })), apiUserId) : Promise.resolve();
            });
        } else {
            return Promise.resolve();
        }
    }

    /**
     * Reads trailer attachments for the lookups (DeviceLink under DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE,
     * TrailerAttachment otherwise) and stores them in the trailerAttachment cache slot that the HOS log
     * views read locally.
     */
    private fetchTrailerAttachments(attachmentLookups: ITrailerAttachmentSearch[], apiUserId: string | null): Promise<ITrailerAttachment[]> {
        if (!attachmentLookups.length) {
            return Promise.resolve([]);
        }
        return new Promise<ITrailerAttachment[]>((resolve, reject) => {
            this.api.multiCall(attachmentLookups.map(toTrailerAttachmentGetCall), (results: unknown[][]) => {
                const fetched = (results || []).reduce((all: ITrailerAttachment[], rows) => all.concat(normalizeTrailerAttachmentResults(rows)), []);
                fetched.length && this.trailerAttachmentCache.add(fetched as TrailerAttachment[], apiUserId ?? undefined);
                resolve(fetched);
            }, errorObject => {
                handleApi(errorObject);
                reject(errorObject);
            });
        });
    }

    private preloadStatusData(localLogs: IIdDictionary<DutyStatusLog>) {
        return this.statusCache.fetchStatusData(this.doubleRetentionStartDate, localLogs)
            .catch(error => {
                handleApi(error, true);
                return Promise.resolve(localLogs);
            });
    }

    private resolveAddConflicts(localLogs, duplicateId) {
        return new Promise((resolve, reject) => {
            let calls: any[] = [];

            for (let logId in localLogs) {
                let log = localLogs[logId];
                if (!log.id && !log.placeholder) {
                    throw new Error("Attempting to sync a record without an ID");
                }

                if (!log.placeholder && log.modified && log.modified === "Add") {
                    calls.push(["Get", {
                        typeName: "DutyStatusLog",
                        search: {
                            id: log.id,
                            includeModifications: true
                        }
                    }]);
                }
            }

            if (calls.length > 0) {
                this.api.multiCall(calls, results => {
                    for (let i = 0; i < results.length; i++) {
                        let logs = results[i];
                        logs.sort((a, b) => b.version < a.version ? -1 : 1);

                        if (logs.length > 0 && localLogs.hasOwnProperty(logs[0].parentId)) {
                            if (logs[0].state === "Active") {
                                // Log has already been added call Set.
                                localLogs[logs[0].parentId].modified = "Set";
                            } else {
                                // If we try to Add but the log has already been added and removed, just delete from localLogs.
                                delete localLogs[logs[0].parentId];
                            }
                        }
                    }
                    this.pushChangedToServer(localLogs).then(resolve, reject);
                }, () => {
                    // Try to use the old method if the call fails
                    localLogs[duplicateId].modified = "Set";
                    this.pushChangedToServer(localLogs).then(resolve, reject);
                });
            } else {
                resolve(localLogs);
            }
        });
    }

    private resetInvalidEdit(localLogs, invalidLog) {
        return new Promise((resolve, reject) => {
            let logId = this.getLogId(invalidLog);

            if (!logId) {
                reject(new Error("Log has no id- cannot resolve invalid edit."));
                return;
            }

            // Get the server version of the bad log
            // If this fails then we won't really know what to do, so fail the sync with reject.
            this.api.call("Get", {
                typeName: "DutyStatusLog",
                search: {
                    id: logId,
                    includeModifications: true
                }
            }, logs => {
                // If we successfully completed this API call, delete the log by id.
                // id might be equal to invalidLog.id in which case this may be redundant, but JS won't throw if you try deleting something you already deleted
                // handles the case where the carrier edits fail and we need to delete the requested edit set.
                delete localLogs[invalidLog.id];
                if (logs.length === 0) {
                    // Log was either deleted or hasn't been added yet. Can't correct the log, so it has to be deleted.
                    delete localLogs[logId];
                } else {
                    // There should only be one. This is the 'good' copy of the log. Replace our local (bad) copy with this log and try pushing again.
                    let convertedLogs = this.dutyStatusLogService.convertDutyStatusLogsFromServer(logs, null);
                    let serverLog = convertedLogs[0];
                    if (!serverLog) {
                        logger.error(logger.Source.SYNC, `Can't save HOS log (id='${ logId }' because could not convert dutyStatusLog with modifications from server.`, this.currentUser);
                        sendToErrorTracker(new Error(`Could not convert dutyStatusLog with modifications from server.`), "javascript");
                        delete localLogs[logId];
                    } else if (serverLog.state === "Inactive" || serverLog.state === "Rejected") {
                        delete localLogs[logId];
                    } else if (this.currentUser && serverLog.driver && this.currentUser.id === serverLog.driver.id) {
                        localLogs[logId] = serverLog;
                    } else {
                        delete localLogs[logId];
                    }
                }
                resolve(localLogs);
            }, reject);
        });
    }

    private deleteInvalidAutomaticLogs(localLogs: IIdDictionary<DutyStatusLog>) {
        let hosLog: DutyStatusLog;

        for (let key in localLogs) {
            if (localLogs.hasOwnProperty(key)) {
                hosLog = localLogs[key];
                if (hosLog.origin === "Automatic" && !this.isAutomaticStatusAllowed(hosLog.status)) {
                    delete localLogs[key];
                }
            }
        }

        this.setLogs(localLogs);
    }

    private deleteInvalidRuleSetRelatedLogs(localLogs: IIdDictionary<DutyStatusLog>) {
        let hosLog: DutyStatusLog;

        for (let key in localLogs) {
            if (localLogs.hasOwnProperty(key)) {
                hosLog = localLogs[key];
                if (CYCLE_AND_OPERATING_ZONE_STATUSES.indexOf(hosLog.status) > -1 && !hosLog.userHosRuleSet) {
                    delete localLogs[key];
                }
            }
        }

        this.setLogs(localLogs);
    }

    private deleteResolvedIoxAction(currentActions: IIoxActionDictionary, actionDateTime: string): void {
        delete currentActions[actionDateTime];
        if (this.store.dispatch) { // redux is not available in JS unit tests
            this.store.dispatch(DataActions.setIoxLogAction(currentActions));
        }
    }

    private resolveCoDriverRemoved(localLogs: IIdDictionary<DutyStatusLog>, logId: string): Promise<IIdDictionary<DutyStatusLog>> {
        let log = localLogs[logId];
        if (!log || !log.coDrivers) {
            return Promise.resolve(localLogs);
        }
        return this.api.multiCallPromise(log.coDrivers.map(coDriver => [
            "Get", {
                typeName: "User",
                search: { id: coDriver.id }
            }] as TMultiCallParam))
        .then(results => {
            let activeCoDrivers = Array.prototype.concat.apply([], results).map(coDriver => {
                return { id: coDriver.id };
            });

            // get log again in case it changed
            localLogs[logId].coDrivers = activeCoDrivers;
            return localLogs;
        });
    }

    private isAutomaticStatusAllowed(value: THosStatus) {
        let statuses = [
            "ON",
            "OFF",
            "D",
            "SB",
            "Exemption16H",
            "AdverseWeather",
            "PC",
            "PC_Exempted",
            "YM",
            "WT",
            "Authority",
            "Certify",
            "EnginePowerup",
            "EngineShutdown",
            "EnginePowerupPC",
            "EngineShutdownPC",
            "PowerCompliance",
            "EngineSyncCompliance",
            "TimingCompliance",
            "PositioningCompliance",
            "DataRecordingCompliance",
            "DataTransferCompliance",
            "OtherCompliance",
            "MissingElementCompliance",
            "UnidentifiedDrivingCompliance",
            "INT_PC",
            "INT_D",
            "SituationalDrivingClear",
            "ExemptionOffDutyDeferral",
            "MotionStopped"
        ];
        return statuses.indexOf(value) > -1;
    }

    private getLogLocations(localLogs: IIdDictionary<DutyStatusLog>) {
        return new Promise<any>((resolve, _) => {
            let logsThatNeedLocation: DutyStatusLog[] = [];
            let logAddresses: string[] = [];
            let logId;

            // collect array of logs that need location coordinates & addresses (two separate arrays, as one will be used in the api call)
            Object.keys(localLogs).forEach(id => {
                let log = <any>localLogs[id];
                if (!log._address) {
                    if (log.location?.address?.formattedAddress) {
                        log._address = log.location.address.formattedAddress;
                    }
                }

                if (log._address && log._address !== "..."
                    && (!log.location || (log.location && log.location.address))
                    && (!common.hasKnownDevice(log) || log.malfunction === "Diagnostic" || log.malfunction === "Malfunction")
                ) {
                    logsThatNeedLocation.push(log);
                    logAddresses.push(log.location?.address?.formattedAddress ? log.location.address.formattedAddress : log._address);
                }
            });

            // no addresses to find
            if (logsThatNeedLocation.length === 0) {
                resolve(localLogs);
            } else {
                for (let i = 0; i < logsThatNeedLocation.length; i++) {
                    let log = logsThatNeedLocation[i];
                    logId = log.id;

                    localLogs[logId].location = {
                        address: {
                            formattedAddress: log._address
                        }
                    };

                    this.markLogAsManualLogOnMalfunction(localLogs, logId);
                }
                resolve(localLogs);
            }
        });
    }

    private markLogAsManualLogOnMalfunction(localLogs: IIdDictionary<DutyStatusLog>, logId: string) {
        let malfunction = localLogs[logId].malfunction;
        switch (malfunction) {
            case "Diagnostic":
                localLogs[logId].malfunction = "DiagnosticManualPosition";
                localLogs[logId].modified = "Set";
                break;
            case "Malfunction":
                localLogs[logId].malfunction = "MalfunctionManualPosition";
                localLogs[logId].modified = "Set";
                break;
            default:
                break;
        }
    }

    private handlePositionMalfunctions(localLogs: IIdDictionary<DutyStatusLog>, currentUser) {
        return new Promise<any>((resolve) => {
            let tempLogs: any = this.dutyStatusLogService.getArray(localLogs);
            let logs: DutyStatusLog [] = [];
            let currentVehicle = getCurrentVehicle();

            tempLogs.forEach(log => {
                logs.push(new DutyStatusLog(log));
            });

            logs.sort(dateTimeComparator());

            let lastUnresolvedPositionMalfunction: { [deviceId: string]: any | null } = {};
            let lastUnresolvedPositionDiagnostic: { [deviceId: string]: any | null } = {};
            let lastUnresolvedMissingDataCompliance: any | null = null;

            let logsNeedingManualInput = [] as any;
            let hasNewNeedingInputLog = false;
            let latestStandardStatusLog = this.dutyStatusLogService.getLatestLog(logs, undefined, (log: DutyStatusLog) => log.isGraphLog() && !log.isPlaceholder());
            let shouldNotUpdateMalfunctionDiagnostic = (log: DutyStatusLog) => log.isMalfunctionLog();
            let shouldPromptUser = (log: DutyStatusLog) => log.isNormalLog() && !log.isIntermediateLog();

            const processComplianceState = (log: DutyStatusLog, deviceId: string): boolean => {
                if (!log.malfunction) {
                    return true;
                }

                const isActive = !log.malfunction.includes("Clear");

                if (log.isPositionComplianceLog()) {
                    if (log.isDiagnostic()) {
                        lastUnresolvedPositionDiagnostic[deviceId] = isActive ? log : null;
                    } else if (log.isMalfunction()) {
                        lastUnresolvedPositionMalfunction[deviceId] = isActive ? log : null;
                    }
                    return true;
                }

                if (log.isMissingElementComplianceLog() && currentVehicle) {
                    const matchesCurrent = deviceId === currentVehicle.id && deviceId !== NO_DEVICE_ID;
                    const isNoDeviceAllowed = deviceId === NO_DEVICE_ID;
                    if (matchesCurrent || isNoDeviceAllowed) {
                        lastUnresolvedMissingDataCompliance = isActive ? log : null;
                        return true;
                    }
                }
                return false; // continue normal processing
            };

            // Logs are in chronological order
            logs.forEach(log => {
                const deviceId = log.getDeviceId() || NO_DEVICE_ID;
                if (processComplianceState(log, deviceId)) {
                    return;
                }
                if (shouldNotUpdateMalfunctionDiagnostic(log)) {
                    return; // Don't care about non-ELD or unidentified event logs
                }

                const diagnosticForDevice = lastUnresolvedPositionDiagnostic[deviceId];
                const malfunctionForDevice = lastUnresolvedPositionMalfunction[deviceId];

                // Require unresolved position diagnostic/malfunction and not already manual position
                if (!(diagnosticForDevice || malfunctionForDevice) || (log.malfunction && log.malfunction.indexOf("ManualPosition") !== -1)) {
                    return;
                }

                const localLog = localLogs[log.id];
                if (!localLog) {
                    return;
                }

                if (malfunctionForDevice && !diagnosticForDevice) {
                    // Only an active malfunction (no diagnostic) -> just mark and exit
                    localLog.malfunction = malfunctionForDevice.malfunction;
                    return;
                }

                if (shouldPromptUser(log)) {
                    logsNeedingManualInput.push(log);
                    if (log.malfunction === "None") {
                        hasNewNeedingInputLog = true;
                    }
                }

                // Locally set this log as malfunction/diagnostic to avoid re-prompting
                localLog.malfunction = malfunctionForDevice ? malfunctionForDevice.malfunction : diagnosticForDevice.malfunction;
                delete localLog.location; // remove location from position diagnostic-affected log
            });

            if (hasNewNeedingInputLog) {
                // Trigger a prompt to populate logs
                this.setLogs(localLogs);
                dispatcher.trigger(DispatcherEvents.SyncHosLocationManual);
            }

            // Check to see if any standard log is missing location (including newly created ones that might not be in the sorted list yet)
            const hasMissingLocationLog = Object.keys(localLogs).some(logId => {
                const log = localLogs[logId];
                return log
                    && !log.placeholder
                    && !log.location
                    && log.status !== "MissingElementCompliance"
                    && (log.driver?.id === currentUser?.id || log.driver?.id === NO_USER_ID)
                    && !log.isMalfunctionLog();
            });

            // Used to determine if at least one log that requires a location isn't the current D log
            let isOtherLogMissingLocation = function(logId, logList) {
                for (let i = 0; i < logList.length; i++) {
                    if (logList[i].id !== logId) {
                        return true;
                    }
                }
                return false;
            };

            // Need to check if there are more than 1 because the latest log would not have been handleable yet
            if (logsNeedingManualInput.length > 0 && latestStandardStatusLog) {
                logsNeedingManualInput = this.dutyStatusLogService.getSortedHosLogs(logsNeedingManualInput, true);
                let lastLogIsDriving = latestStandardStatusLog.status === "D" && latestStandardStatusLog.origin === "Automatic";
                // Check if we need to add a data missing diagnostic log here if the latest is a driving status from the server
                // As well, only create a missing data compliance if we're not currently in missing data compliance state and there is at least one log other than the current auto D that requires a location

                // Additionally, skip the creation of missing data compliance, if under CarrierExemption ruleset
                const isCarrierExemptionRuleset = this.hosProcess.isLatestRulesetCarrierExemption();

                if (
                    !lastUnresolvedMissingDataCompliance &&
                    lastLogIsDriving &&
                    isOtherLogMissingLocation(
                        latestStandardStatusLog.id,
                        logsNeedingManualInput
                    ) &&
                    !isCarrierExemptionRuleset
                ) {
                    // OK, so this driver is now driving and has not filled out some addresses 😱
                    // Let's log that!
                    const logsNeedingManualInputTruncated = logsNeedingManualInput.map(log => ({
                        dateTime: log.dateTime,
                        status: log.status,
                        editDateTime: log.editDateTime
                    }));
                    logger.log(logger.Source.SYNC, "Creating missing compliance log, logs missing element data: " + JSON.stringify(logsNeedingManualInputTruncated));
                    let firstNeedingAddressLog = logsNeedingManualInput.length > 1 ? logsNeedingManualInput[logsNeedingManualInput.length - 1] : logsNeedingManualInput[0];
                    let newLog = this.getMissingElementComplianceLog(currentUser, firstNeedingAddressLog.getDate(), false, null);
                    if (newLog) {
                        localLogs[newLog.id] = newLog;
                    }
                }
            } else if (
                currentVehicle && currentVehicle.id && currentVehicle.id === "NoDeviceId" &&
                lastUnresolvedMissingDataCompliance &&
                !hasMissingLocationLog) {
                // No vehicle connected: clear the previous missing element compliance once every log has a
                // location, including newly created ones that are not yet in the sorted list.
                let newLog = this.getMissingElementComplianceLog(currentUser, undefined, true, lastUnresolvedMissingDataCompliance);
                if (newLog) {
                    localLogs[newLog.id] = newLog;
                }
            } else if (
                lastUnresolvedMissingDataCompliance &&
                logsNeedingManualInput.length === 0 &&
                !(currentVehicle && currentVehicle.id === "NoDeviceId")) {
                // A real vehicle is connected: clear the previous missing element compliance if all logs have
                // location. Either have 1 or no logs in this "else if".
                // Only clear the missing element if there are no logs needing manual input.
                // Explicitly excludes NoDeviceId rather than relying on getMissingElementComplianceLog's own
                // vehicle check for mutual exclusivity with the NoDeviceId branch above: logsNeedingManualInput
                // (device-scoped) and hasMissingLocationLog (all-logs scan) can disagree, so this branch is
                // reachable even when NoDeviceId has an unresolved missing-location log that should keep the
                // NoDeviceId diagnostic open.
                let newLog = this.getMissingElementComplianceLog(currentUser, undefined, true, null);
                if (newLog) {
                    localLogs[newLog.id] = newLog;
                }
            }
            resolve(localLogs);
        });
    }

    private getMissingElementComplianceLog(currentUser, overrideDate?: Date, isClear = false, lastUnresolvedMissingDataCompliance?: DutyStatusLog | null): DutyStatusLog | null {
        if (lastUnresolvedMissingDataCompliance) {
            return new DutyStatusLog({
                id: lastUnresolvedMissingDataCompliance.id,
                dateTime: lastUnresolvedMissingDataCompliance.dateTime,
                status: "MissingElementCompliance",
                malfunction: "SystemDiagnosticClear",
                device: { id: "NoDeviceId" },
                driver: { id: lastUnresolvedMissingDataCompliance.driver.id },
                origin: "Automatic",
                modified: "Set"
            });
        }
        let currentVehicle = getCurrentVehicle();
        if (currentVehicle && currentVehicle.id && currentVehicle.id !== "NoDeviceId" && currentUser && currentUser.id) {
            return new DutyStatusLog({
                dateTime: (overrideDate || getServerDate()).toISOString(),
                status: "MissingElementCompliance",
                malfunction: isClear ? "SystemDiagnosticClear" : "Diagnostic",
                device: { id: currentVehicle.id },
                driver: { id: "NoUserId" },
                origin: "Automatic",

                // Fields set by Sync.addItem()
                id: idSerializer.get(),
                modified: "Add"
            });
        }
        return null;
    }

    private callDutyStatusGetFeedWithResultLimit(
        userId: string | null | undefined,
        deviceId: string | null | undefined,
        statuses: string[] | null | undefined,
        fromDate: Date | string | null | undefined,
        toDate: Date | string | null | undefined,
        includeBoundaryLogs: boolean | null | undefined,
        fromVersion: string | null,
        includeCompliance: boolean | undefined,
        includeModifications: boolean,
        resultsLimit: number | undefined,
        context: string,
        forUser: IUser | null,
        callback: (logs: DutyStatusLog[], toVersion: string) => void,
        errorCallback?: (err: IApiError) => void
    ): void {
        const queryParams: IDutyStatusLogQueryParams = {
            typeName: "DutyStatusLog",
            ...(fromVersion !== null && { fromVersion }),
            search: {
                userSearch: userId ? { id: userId } : null,
                deviceSearch: deviceId ? { id: deviceId } : null,
                statuses,
                fromDate,
                toDate,
                includeBoundaryLogs,
                includeCompliance,
                includeModifications,
                includeLocations: true
            },
            resultsLimit
        };
        this.api.call("GetFeed", queryParams, result => {
            const logs = this.dutyStatusLogService.convertDutyStatusLogsFromServer(
                result.data as DutyStatusLog[],
                forUser
            );
            this.checkAndLogResultsTruncation(logs, resultsLimit, context);
            callback(logs, result.toVersion);
        }, errorCallback);
    }

    private checkAndLogResultsTruncation(logs: DutyStatusLog[], resultsLimit: number | undefined, context: string): void {
        // The API returns at most `resultsLimit` results, so logs.length can equal but never exceed it.
        // We fire when logs.length === resultsLimit because we cannot distinguish "exactly N logs exist"
        // from "N+ logs were truncated to N". This will fire for users with exactly N logs (no actual
        // data loss), but is acceptable to avoid missing true truncation events.
        if (logs.length === resultsLimit) {
            logger.event(
                logger.Event.EXCESSIVEDUTYSTATUSLOGS,
                `Downloaded HOS logs meet or exceed the resultsLimit (${ resultsLimit }) for context: ${ context }`,
                this.currentUser
            );
        }
    }

    private cleanIncorrectMalfunctionTypes(localLogs) {
        let changed = false;

        return new Promise((resolve) => {
            Object.keys(localLogs).forEach(logId => {
                let log = localLogs[logId];
                if (log.malfunction && MALFUNCTION_TYPES.indexOf(log.malfunction) === -1) {
                    delete localLogs[logId];
                    changed = true;
                }
            });

            if (changed) {
                this.setLogs(localLogs);
            }

            resolve(localLogs);
        });
    }

    private syncDutyStatusLogs(serverLogs: DutyStatusLog[], localLogs: IIdDictionary<DutyStatusLog> = {}, force = false, isFromMalfunctionSync = false): IIdDictionary<DutyStatusLog> {
        // If we are downloading all logs, clear the list of already synced logs
        let newLogs: IIdDictionary<DutyStatusLog> = localLogs;

        if (force) {
            let userHosLogs = localLogs ? extend({}, localLogs) : {};
            newLogs = {};
            if (userHosLogs && !isEmpty(userHosLogs)) {
                hashToArray(userHosLogs).forEach((log: DutyStatusLog) => {
                    if (log.modified || this.isReviewedCarrierAddEdit(log)) {
                        newLogs[log.id] = log;
                    }
                });
            }
        }

        return this.mergeDutyStatusLogs(serverLogs || [], newLogs, force, isFromMalfunctionSync);
    }

    private syncAnnotationLogs(logsToAdd: IAnnotation[] = [], force = false) {
        const apiUserId = this.currentUser?.id;
        if (!apiUserId) {
            throw new Error("Attempting to store annotations for undefined userID");
        }
        let annotationLogs = getClonedAnnotationsForUser(this.store, apiUserId);
        let newLogs: IIdDictionary<IAnnotation> = {};
        let annotationCalls: any[] = [];

        logsToAdd.forEach(log => newLogs[log.id] = log);

        // Merge in server logs with new local logs
        Object.keys(annotationLogs).forEach(logId => {
            let log = annotationLogs[logId];

            if (!log.id) {
                throw new Error("Attempting to sync a record without an ID");
            }

            if (log.modified) {
                let sanitizedLog = extend({}, log);
                delete sanitizedLog.modified;
                delete sanitizedLog.previousState;
                if (log.modified === "Set") {
                    annotationCalls.push(["Set", {
                        typeName: "AnnotationLog",
                        entity: sanitizedLog
                    }]);
                } else if (log.modified === "Add" && !this.pendingAnnotationAdds.has(log.id)) {
                    this.pendingAnnotationAdds.add(log.id);
                    annotationCalls.push(["Add", {
                        typeName: "AnnotationLog",
                        entity: sanitizedLog
                    }]);
                }
                newLogs[log.id] = log.modified === "Add" ? log : sanitizedLog;
            } else if (!force && !newLogs.hasOwnProperty(log.id)) {
                newLogs[log.id] = log;
            }
        });

        annotationLogs = newLogs;
        this.store.dispatch(HosAnnotationActions.setAnnotations({ userId: apiUserId, annotations: annotationLogs }));

        // Each call is sent on its own: a multicall stops at its first error, which would retry the saved
        // calls before it as duplicates and block every call after a permanently failing one.
        annotationCalls.forEach(call => {
            const isAdd = call[0] === "Add";
            const id: string = call[1].entity.id;
            this.api.multiCall([call], () => {
                isAdd && this.markAnnotationAddSaved(apiUserId, id);
                logger.log(logger.Source.SYNC, `Set annotation log ${ id }`, this.currentUser);
            }, e => {
                this.pendingAnnotationAdds.delete(id);
                if (isAdd && isDuplicateException(e)) {
                    // The server already saved this annotation but its response was lost, so the add is acknowledged
                    this.markAnnotationAddSaved(apiUserId, id);
                    return;
                }
                logger.error(logger.Source.SYNC, `Could not set AnnotationLog ${ id }`, this.currentUser, e);
            });
        });
    }

    private markAnnotationAddSaved(apiUserId: string, id: string) {
        this.pendingAnnotationAdds.delete(id);
        const latestAnnotations = getClonedAnnotationsForUser(this.store, apiUserId);
        if (latestAnnotations[id]?.modified === "Add") {
            const saved = extend({}, latestAnnotations[id]);
            delete saved.modified;
            delete saved.previousState;
            latestAnnotations[id] = saved;
            this.store.dispatch(HosAnnotationActions.setAnnotations({ userId: apiUserId, annotations: latestAnnotations }));
        }
    }

    private loadUnidentifiedLogs(force = false): Promise<DutyStatusLog[]> {
        return new Promise<DutyStatusLog[]>((resolve, reject) => {
            let currentVehicle = getCurrentVehicle();
            let previousVehicle = getPreviousVehicle();
            let vehicleChanged = (!previousVehicle || (currentVehicle && currentVehicle.id !== previousVehicle.id));
            let hosFeedVersion = common.getFeedVersion(this.api, this.store, common.FeedTypeKey.HOS);
            let runUnidentifiedSync = force || vehicleChanged || hosFeedVersion === null;
            if (runUnidentifiedSync && currentVehicle !== null && currentVehicle.id !== NO_DEVICE_ID) {
                let retentionStartDate = this.hosProcess.getRetentionStartDate();
                this.callDutyStatusGetFeedWithResultLimit(
                    NO_USER_ID,
                    currentVehicle.id,
                    UNIDENTIFIED_STATUSES,
                    retentionStartDate,
                    null,
                    null,
                    null,
                    true,
                    true,
                    UNIDENTIFIED_RESULTS_LIMIT,
                    "unidentified driver logs",
                    null,
                    (feedLogs) => {
                        let dutyLogs: DutyStatusLog[] = [];
                        let localLogs = this.getLogs() || {};
                        let minTime = retentionStartDate.getTime();

                        logger.log(logger.Source.SYNC, `Downloaded ${ feedLogs.length } unidentified HOS records`, this.currentUser);

                        feedLogs.forEach((log: DutyStatusLog) => {
                            let logTime = new Date(log.dateTime).getTime();

                            if (logTime >= minTime || localLogs[log.id]) {
                                dutyLogs.push(log);
                            }
                        });
                        resolve(dutyLogs);
                    },
                    (err) => {
                        handleApi(err, true);
                        reject(err);
                    }
                );
            } else {
                resolve([]);
            }
        });
    }

    private loadAnnotationsByLogs(dutyLogs: DutyStatusLog[], localLogs: IIdDictionary<DutyStatusLog>, force = false) {
        let apiUserId = this.currentUser ? this.currentUser.id : undefined;
        const annotationLogs = force ? {} : getClonedAnnotationsForUser(this.store, apiUserId);

        return new Promise((resolve, reject) => {
            let annotationCalls: any[] = [];
            let existingAnnotations: IAnnotation[] = [];

            dutyLogs.forEach(log => {
                // Only active logs should have annotations.
                if (ANNOTATED_STATES.indexOf(<THosState>log.state) > -1 && log.annotations) {
                    log.annotations.forEach(anno => {
                        //If this annotation already exists in annotations, don't sync it again
                        if (annotationLogs.hasOwnProperty(anno.id)){
                            existingAnnotations.push(annotationLogs[anno.id]);
                        } else {
                            annotationCalls.push(["Get", {
                                typeName: "AnnotationLog",
                                search: { id: anno.id }
                            }]);
                        }
                    });
                }
            });

            if (annotationCalls.length > 0) {
                this.api.multiCall(annotationCalls, data => {
                    let annotations: IAnnotation[] = data.reduce((a, b) => a.concat(b), []);
                    this.syncAnnotationLogs(
                        existingAnnotations.length > 1 ? annotations.concat(existingAnnotations) : annotations,
                        force
                    );
                    resolve(localLogs);
                }, reject);
            } else {
                resolve(localLogs);
            }
        });
    }

    private isInvalidEdit(errorObject: IApiError) {
        return errorMessages.isInvalidLogEditException(errorObject)
            || errorMessages.isInvalidRequestedLogEditException(errorObject)
            || errorMessages.isDriverAccessException(errorObject)
            || errorMessages.isChangedRequestedByUserException(errorObject)
            || isArgumentException(errorObject);
    }

    private resetLogToCorrectState(logsToMove: ISlimDutyStatusLog[], localLogs: IIdDictionary<DutyStatusLog>) {
        return new Promise((resolve, reject) => {
            let logIdsWithErrors = logsToMove.map(log => log.id);
            this.getLogsByIdFromList(logIdsWithErrors, localLogs, (ids, logDict, logsFromServer) => {
                logsFromServer.forEach((logs, index) => {
                    // Have to do this because the server returns driver = NoUserId as a string value. Second check is just there as a 'just in case'
                    const isActiveNoUserLog = !!logs.find(log => log.state === "Active" && ((log.driver as any) === NOUSERID || log.driver.id === NOUSERID));
                    let convertedLogs = this.dutyStatusLogService.convertDutyStatusLogsFromServer(logs, isActiveNoUserLog ? null : this.currentUser);

                    let id = ids[index];
                    if (convertedLogs.length) {
                        let log = convertedLogs[0];
                        if (log.state === "Inactive" ||
                            (this.currentUser && log.driver.id !== this.currentUser.id && log.requestedEdit?.driver.id !== this.currentUser.id)) {
                            // If the log has no association with the user and isn't an unassigned log of the current vehicle, delete it
                            delete logDict[id];
                        } else {
                            logDict[id] = log;
                        }
                    } else {
                        // Log no longer visible, delete
                        delete logDict[id];
                    }
                });
            }).then(() => {
                this.setLogs(localLogs);
                resolve();
            }, reject);
        });
    }

    private reassignLogs(localLogs: IIdDictionary<DutyStatusLog>): Promise<IIdDictionary<DutyStatusLog>> {
        return new Promise<IIdDictionary<DutyStatusLog>>(resolve => {
            let resolvedDriverChangeRequests: { [driverId: string]: DutyStatusLog[] } = {};
            Object.keys(localLogs).forEach(logId => {
                let log = localLogs[logId];
                if (!log.placeholder && log.originalDriverBeforeEdit && (!log.requestedEdit || log.requestedEdit.state === "Active")) {
                    let driverId = `${ log.originalDriverBeforeEdit }-${ log.originalDriverBeforeEdit === NOUSERID ? log.device.id : "" }`;
                    if (resolvedDriverChangeRequests.hasOwnProperty(driverId)) {
                        resolvedDriverChangeRequests[driverId].push(log);
                    } else {
                        resolvedDriverChangeRequests[driverId] = [log];
                    }
                }
            });

            let driverIds = Object.keys(resolvedDriverChangeRequests);
            if (!driverIds.length) {
                resolve(localLogs);
                return;
            }

            let calls: ["ReassignDutyStatusLogs", IReassignApiParams][] = [];
            driverIds.forEach(id => {
                const driverId = id.split("-")[0];
                resolvedDriverChangeRequests[id].sort(dateTimeComparator());
                calls.push(["ReassignDutyStatusLogs", {
                    logsToMove: resolvedDriverChangeRequests[id].map(log => {
                        let logClone = log.requestedEdit || log;
                        return {
                            id: log.id,
                            annotations: logClone.annotations,
                            verifyDateTime: logClone.verifyDateTime,
                            editDateTime: logClone.editDateTime
                        };
                    }),
                    isUnassigningLogs: driverId === this.currentUser?.id
                }]);
            });
            return this.pushReassignedLogsToServer(calls, localLogs, resolvedDriverChangeRequests, driverIds).then(resolve, resolve);
        });
    }

    private pushReassignedLogsToServer(
        calls: [string, IReassignApiParams][],
        localLogs: IIdDictionary<DutyStatusLog>,
        resolvedDriverChangeRequests: { [driverId: string]: DutyStatusLog[] },
        resolvedDriverIds: string[],
        retryCount = 3
    ) {
        return new Promise(resolve => {
            let handleSuccess = (resolvedDrivers: string[]) => {
                let currentVehicle = getCurrentVehicle();
                for (let id of resolvedDrivers) {
                    const driverId = id.split("-")[0];
                    let editedLogs = resolvedDriverChangeRequests[id];
                    for (let log of editedLogs) {
                        if (driverId === this.currentUser?.id && localLogs.hasOwnProperty(log.id) && localLogs[log.id].device.id !== currentVehicle?.id) {
                            delete localLogs[log.id];
                        } else {
                            this.cleanRequestedLog(log);
                        }
                    }
                }
                this.setLogs(localLogs);
            };

            // If we have retried too many times, exit and continue next sync
            if (calls.length === 0 || retryCount <= 0) {
                resolve(localLogs);
                return;
            }

            this.api.multiCall(calls, () => {
                handleSuccess(resolvedDriverIds);
                resolve(localLogs);
            }, errorObject => {
                let newCalls: [string, IReassignApiParams][] = calls.map(call => [call[0], { ...call[1] }]);
                let removeFailedCall = () => {
                    newCalls.splice(0, 1);
                    resolvedDriverIds.splice(0, 1);
                };

                let requestIndex = errorObject?.data?.requestIndex;
                let erroringCall = requestIndex !== undefined && newCalls[requestIndex] && newCalls[requestIndex][1];

                // Handle any successfully completed calls so they are not re-synced
                if (requestIndex > 0) {
                    newCalls.splice(0, requestIndex);
                    handleSuccess(resolvedDriverIds.splice(0, requestIndex));
                }

                if (isOverLimitException(errorObject)) {
                    resolve(localLogs);
                    return;
                }

                // There is something wrong with the reassignment and the context may have changed. Reset the erroring logs to their server state and move on.
                if (erroringCall
                    && (this.isInvalidEdit(errorObject)
                        || isInvalidApiOperationException(errorObject)
                        || isInvalidRequestException(errorObject)
                        || isRelationViolatedException(errorObject) && errorMessages.isNotInSystemException(errorObject))) {
                    logger.error(logger.Source.SYNC, `Error: trying to reassign log to driver: ${ resolvedDriverIds[0] }, reason: ${ errorObject.message }`, this.currentUser);

                    return this.resetLogToCorrectState(erroringCall.logsToMove, localLogs)
                        .then(() => {
                            removeFailedCall();
                            return this.pushReassignedLogsToServer(newCalls, localLogs, resolvedDriverChangeRequests, resolvedDriverIds, retryCount - 1).then(resolve, resolve);
                        }, resetLogErrorObject => {
                            handleApi(resetLogErrorObject, true);
                            resolve(localLogs);
                        });
                }

                // Can't determine the problem... give up for now and continue
                handleApi(errorObject, true);
                resolve(localLogs);
                return;
            });
        });
    }

}
